import { useState, useMemo, useRef, useEffect, useCallback, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { Card } from '../App';
import { ManaCurveChart } from './ManaCurveChart';
import { LandProbabilityTable } from './LandProbabilityTable';
import { ColorDistributionChart } from './ColorDistributionChart';
import { BrewTooltip } from './BrewTooltip';
import { NumericStepper } from './NumericStepper';
import { SuggestedCardsSection } from './SuggestedCardsSection';
import { DropCurveColumn } from './DropColumnCardTile';
import { CardSearch } from './CardSearch';
import { DecklistImport } from './DecklistImport';
import { DeckGrid } from './DeckGrid';
import { OverviewTab } from './OverviewTab';
import { PlanAnalysis } from './PlanAnalysis';
import { DiscoverTab } from './DiscoverTab';
import { SessionTab } from './SessionTab';
import { NewsTab } from './NewsTab';
import { EventsTab } from './EventsTab';
import { MarketTab } from './MarketTab';
import { FAQPage } from './FAQPage';
import { LandingView } from './LandingView';
import { SaveDeckModal } from './SaveDeckModal';
import { CollapsibleSection } from './CollapsibleSection';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { generateLandSuggestions, generateMissingLandSuggestions } from '../lib/landAutoSuggest';
import { useResolvedCardImage } from '../lib/scryfall';
import { ManaSymbol } from './ManaSymbol';
import { SmartAdviceBox } from './SmartAdviceBox';
import { NavDropdown, type TabGroup } from './NavDropdown';
import { authService, roleService, sparksService, supabase } from '../lib/supabase';
import type { CompleteAnalysis } from '../lib/wasm-types';
import { Loader2, Droplets, Palette, FlaskConical, LayoutGrid, List, X, Upload, Copy, Camera, AlertTriangle, Users, Timer, Compass, FileDown, Hand, ClipboardList, RefreshCw, Newspaper, Calendar, ShoppingCart, Globe, Wrench, Radio, HelpCircle } from 'lucide-react';
import html2canvas from 'html2canvas';
import { probabilityLandsByTurn, cardsSeenByTurn, getProbabilityColorClass, formatProbability, probabilityAtLeastOne, hypergeometricCDFTail, combinedPlayability, combinedPlayabilitySeparateTurns } from '../lib/probability';


// MTG color palettes for dynamic plan colors
const MTG_PALETTES = {
  W: { shades: ['#fef3c7', '#fde68a', '#fcd34d', '#fbbf24', '#f59e0b'] },
  U: { shades: ['#bae6fd', '#7dd3fc', '#38bdf8', '#0ea5e9', '#0284c7'] },
  B: { shades: ['#d4d4d8', '#a1a1aa', '#71717a', '#52525b', '#3f3f46'] },
  R: { shades: ['#fca5a5', '#f87171', '#ef4444', '#dc2626', '#b91c1c'] },
  G: { shades: ['#86efac', '#4ade80', '#22c55e', '#16a34a', '#15803d'] },
  C: { shades: ['#e2e8f0', '#cbd5e1', '#94a3b8', '#64748b', '#475569'] },
};

// Get dynamic plan colors based on deck's color identity
function getDynamicPlanColors(deck: Card[]) {
  const WUBRG = ['W', 'U', 'B', 'R', 'G'] as const;
  const colorSet = new Set<string>();

  deck.forEach(card => {
    card.colors?.forEach(color => {
      if (WUBRG.includes(color as typeof WUBRG[number])) {
        colorSet.add(color);
      }
    });
  });

  const deckColors = WUBRG.filter(c => colorSet.has(c));
  const palettes = deckColors.length > 0
    ? deckColors.map(c => MTG_PALETTES[c])
    : [MTG_PALETTES.C];

  const plans = ['A', 'B', 'C', 'D', 'E'];
  const colors: Record<string, { hex: string; bg: string; text: string; border: string }> = {};

  plans.forEach((plan, i) => {
    const palette = palettes[i % palettes.length];
    const shade = palette.shades[Math.floor(i / palettes.length) % 5];
    colors[plan] = {
      hex: shade,
      bg: `rgba(${parseInt(shade.slice(1, 3), 16)}, ${parseInt(shade.slice(3, 5), 16)}, ${parseInt(shade.slice(5, 7), 16)}, 0.2)`,
      text: shade,
      border: `rgba(${parseInt(shade.slice(1, 3), 16)}, ${parseInt(shade.slice(3, 5), 16)}, ${parseInt(shade.slice(5, 7), 16)}, 0.3)`,
    };
  });

  return colors;
}

interface AnalyticsPanelProps {
  deck: Card[];
  sideboard?: Card[];
  landCount: number;
  totalCards?: number;
  analysis: CompleteAnalysis | null;
  isAnalyzing: boolean;
  inputMode?: 'search' | 'import';
  deckName?: string;
  format?: string;
  activePlan?: string;
  onActivePlanChange?: (plan: string) => void;
  onCardClick?: (card: Card) => void;
  onUpdateCard?: (cardId: string, updates: Partial<Card>) => void;
  onRemoveCard?: (cardId: string) => void;
  onAddCard?: (card: Card) => void;
  onAddToSideboard?: (card: Card) => void;
  onInputModeChange?: (mode: 'search' | 'import') => void;
  onUpdatePlanQuantity?: (cardId: string, plan: 'A' | 'B' | 'C' | 'D' | 'E', mainQuantity: number) => void;
  onUpdatePlanMV?: (cardId: string, plan: 'A' | 'B' | 'C' | 'D' | 'E', mv: number) => void;
  onUpdatePlanCopyMVs?: (cardId: string, plan: 'A' | 'B' | 'C' | 'D' | 'E', mvs: number[]) => void;
  onUpdatePlanCopySideboard?: (cardId: string, plan: 'A' | 'B' | 'C' | 'D' | 'E', sbs: boolean[]) => void;
  onUpdatePlanCopyFace?: (cardId: string, plan: 'A' | 'B' | 'C' | 'D' | 'E', faces: number[]) => void;
  onMoveToSideboard?: (cardId: string) => void;
  onMoveToMainDeck?: (cardId: string) => void;
  onUpdateSideboardCard?: (cardId: string, updates: Partial<Card>) => void;
  onRemoveFromSideboard?: (cardId: string) => void;
  onFormatDetect?: (format: string) => void;
  onClearDeck?: () => void;
  planTargetTurns?: Record<string, number>;
  onUpdatePlanTargetTurns?: (turns: Record<string, number>) => void;
  onLoadDeckFromDiscover?: (cards: Record<string, number>, lands: Record<string, number>, sideboard: Record<string, number>, name: string) => void;
  onOpenAuth?: () => void;
}

type TabType = 'landing' | 'news' | 'events' | 'build' | 'distribution' | 'queue' | 'session' | 'discover' | 'market' | 'faq';
type ViewMode = 'dynamic' | 'list' | 'grid';
type GroupBy = 'none' | 'type' | 'mv' | 'color' | 'name';

// ─── Path routing: URL-friendly handles → internal tab IDs ────────
const PATH_TO_TAB: Record<string, TabType> = {
  '': 'landing',
  '/': 'landing',
  landing: 'landing',
  news: 'news',
  events: 'events',
  brew: 'build',
  build: 'build',
  discover: 'discover',
  'discover-decks': 'discover',
  'discover-streamers': 'discover',
  market: 'market',
  session: 'session',
  faq: 'faq',
  distribution: 'distribution',
  queue: 'queue',
};

const TAB_TO_PATH: Record<TabType, string> = {
  landing: '',
  news: 'news',
  events: 'events',
  build: 'brew',
  distribution: 'distribution',
  queue: 'queue',
  session: 'session',
  discover: 'discover',
  market: 'market',
  faq: 'faq',
};

function getTabFromPath(): TabType {
  const pathname = window.location.pathname.replace(/^\/+|\/+$/g, '').toLowerCase();
  const firstSegment = pathname.split('/')[0];

  if (firstSegment && PATH_TO_TAB[firstSegment]) {
    return PATH_TO_TAB[firstSegment];
  }

  if (!firstSegment && window.location.hash) {
    const rawHash = window.location.hash.replace(/^#\/?/, '').toLowerCase();
    const hashSegment = rawHash.split('/')[0];
    if (PATH_TO_TAB[hashSegment]) {
      return PATH_TO_TAB[hashSegment];
    }
  }

  return firstSegment === '' ? 'landing' : 'news';
}

interface HoverCardPreviewProps {
  hoveredCard: Card;
  mousePos: { x: number; y: number };
}

function HoverCardPreview({ hoveredCard, mousePos }: HoverCardPreviewProps) {
  const isFlipped = hoveredCard.isFlipped && Boolean(hoveredCard.backImageUrl || hoveredCard.backName);
  const targetName = isFlipped && hoveredCard.backName
    ? hoveredCard.backName
    : (hoveredCard.name.includes(' // ') ? hoveredCard.name.split(' // ')[0] : hoveredCard.name);
  const initialUrl = isFlipped && hoveredCard.backImageUrl ? hoveredCard.backImageUrl : hoveredCard.imageUrl;
  const resolvedImgSrc = useResolvedCardImage(targetName, initialUrl);

  const cardWidth = 160;
  const gap = 30;
  const left = mousePos.x > window.innerWidth / 2
    ? Math.max(10, mousePos.x - cardWidth - gap)
    : Math.min(window.innerWidth - cardWidth - 10, mousePos.x + gap);
  const cardHeight = 280;
  const padding = 20;
  const idealTop = mousePos.y - cardHeight / 2;
  const top = Math.max(padding, Math.min(window.innerHeight - cardHeight - padding, idealTop));

  return (
    <div
      className="fixed z-50 pointer-events-none"
      style={{ left, top }}
    >
      <ImageWithFallback
        src={resolvedImgSrc}
        alt={hoveredCard.name}
        className={`w-40 h-auto rounded-lg shadow-2xl border-2 ${isFlipped ? 'border-purple-500' : 'border-white/20'}`}
      />
    </div>
  );
}

export function AnalyticsPanel({
  deck: deckProp,
  sideboard = [],
  landCount,
  totalCards: propTotalCards,
  analysis,
  isAnalyzing,
  inputMode = 'import',
  deckName = 'Untitled Deck',
  format = 'Constructed 60',
  activePlan: propActivePlan,
  onActivePlanChange,
  onCardClick,
  onUpdateCard,
  onRemoveCard,
  onAddCard,
  onAddToSideboard,
  onInputModeChange,
  onUpdatePlanQuantity,
  onUpdatePlanMV,
  onUpdatePlanCopyMVs,
  onUpdatePlanCopySideboard,
  onUpdatePlanCopyFace,
  onMoveToSideboard,
  onMoveToMainDeck,
  onUpdateSideboardCard,
  onRemoveFromSideboard,
  onFormatDetect,
  onClearDeck,
  planTargetTurns = {},
  onUpdatePlanTargetTurns,
  onLoadDeckFromDiscover,
  onOpenAuth
}: AnalyticsPanelProps) {
  // Runtime fix for DFCs incorrectly marked as lands
  // If type contains "//" and front face isn't a land, correct isLand to false
  const deck = useMemo(() => deckProp.map(card => {
    if (card.isLand && card.type?.includes('//')) {
      const frontFace = card.type.split('//')[0].trim().toLowerCase();
      if (!frontFace.includes('land')) {
        return { ...card, isLand: false };
      }
    }
    return card;
  }), [deckProp]);

  // Helper: Is this card effectively a land?
  // True if: card.isLand, OR (MDFC flipped to land back)
  // Transform cards when flipped DON'T become lands - they still cost mana to cast!
  const isEffectivelyLand = (card: Card): boolean => {
    if (card.isLand) return true;
    // Only modal_dfc flipped to land back counts as a land
    // Transform cards (like Brass's Tunnel-Grinder) must be cast first, so they stay as spells
    if (card.isFlipped && card.layout === 'modal_dfc' && card.backType?.toLowerCase().includes('land')) {
      return true;
    }
    return false;
  };

  // Helper: Is this a flipped transform card that becomes a land?
  // These count as +1 virtual land (mid-game land sources) but still cost their MV to cast
  const isTransformLandSource = (card: Card): boolean => {
    return !!(card.isFlipped && card.layout === 'transform' && card.backType?.toLowerCase().includes('land'));
  };

  const totalCards = deck.reduce((sum, c) => sum + c.quantity, 0);
  const sideboardCount = sideboard.reduce((sum, c) => sum + c.quantity, 0);
  const [activeTab, setActiveTabState] = useState<TabType>(getTabFromPath);

  // Sync path ↔ tab
  const setActiveTab = useCallback((tab: TabType, replaceState = false) => {
    setActiveTabState(tab);
    const handle = TAB_TO_PATH[tab];
    const targetUrl = handle ? `/${handle}` : '/';

    if (window.location.pathname !== targetUrl) {
      if (replaceState) {
        window.history.replaceState(null, '', targetUrl);
      } else {
        window.history.pushState(null, '', targetUrl);
      }
    }
  }, []);

  // Listen for browser back/forward and clean legacy hash URLs
  useEffect(() => {
    // If loaded via legacy hash (e.g. /#brew), rewrite to clean path /brew
    if (window.location.hash && (window.location.pathname === '/' || window.location.pathname === '')) {
      const initialTab = getTabFromPath();
      if (initialTab !== 'landing') {
        const handle = TAB_TO_PATH[initialTab];
        window.history.replaceState(null, '', `/${handle}`);
      }
    }

    const onPopState = () => {
      const tab = getTabFromPath();
      setActiveTabState(tab);
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);
  // Tab slider state for premium navigation
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const navRef = useRef<HTMLDivElement>(null);
  const [sliderStyle, setSliderStyle] = useState<CSSProperties>({});

  // Update slider position when active tab changes
  useEffect(() => {
    const updateSlider = () => {
      const activeEl = tabRefs.current[activeTab];
      const navEl = navRef.current;
      if (activeEl && navEl) {
        const navRect = navEl.getBoundingClientRect();
        const tabRect = activeEl.getBoundingClientRect();
        setSliderStyle({
          left: tabRect.left - navRect.left,
          width: tabRect.width,
        });
      }
    };
    updateSlider();
    // Also update on resize
    window.addEventListener('resize', updateSlider);
    return () => window.removeEventListener('resize', updateSlider);
  }, [activeTab]);

  const [showImportModal, setShowImportModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);

  // Portal target for nav dropdown in header
  const [navSlot, setNavSlot] = useState<HTMLElement | null>(null);
  useEffect(() => {
    // Find the header slot after mount
    const el = document.getElementById('header-nav-slot');
    if (el) setNavSlot(el);
  }, []);
  const [viewMode, setViewMode] = useState<ViewMode>('dynamic'); // Dynamic (MVD Grid) is default
  const [groupBy, setGroupBy] = useState<GroupBy>('type');
  // Use controlled state if provided, otherwise internal state
  const [internalActivePlan, setInternalActivePlan] = useState<string>('base');
  const activePlan = propActivePlan ?? internalActivePlan;
  const setActivePlan = (plan: string) => {
    if (onActivePlanChange) {
      onActivePlanChange(plan);
    } else {
      setInternalActivePlan(plan);
    }
  };
  const [hoveredCard, setHoveredCard] = useState<Card | null>(null);
  const [showBackFace, setShowBackFace] = useState(false); // Track if showing back face of DFC
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  // Reset back face when card changes
  useEffect(() => {
    setShowBackFace(false);
  }, [hoveredCard?.id]);

  // Clear hovered card if it no longer exists in deck (prevents stuck tooltip when card removed)
  useEffect(() => {
    if (hoveredCard && !deck.some(c => c.id === hoveredCard.id) && !sideboard.some(c => c.id === hoveredCard.id)) {
      setHoveredCard(null);
    }
  }, [deck, sideboard, hoveredCard]);

  // Broadcast hovered card to OBS overlay via Supabase Realtime
  useEffect(() => {
    // Dynamic import to avoid SSR issues
    import('../lib/supabase').then(({ overlayService }) => {
      if (hoveredCard && hoveredCard.imageUrl) {
        // Send the appropriate face based on card.isFlipped state
        const isFlipped = hoveredCard.isFlipped;
        const imageToShow = isFlipped && hoveredCard.backImageUrl
          ? hoveredCard.backImageUrl
          : hoveredCard.imageUrl;

        // Get just the single face name (card.name may contain "FrontName // BackName")
        let nameToShow: string;
        if (isFlipped && hoveredCard.backName) {
          nameToShow = hoveredCard.backName;
        } else if (hoveredCard.name.includes(' // ')) {
          // Extract just the front name
          nameToShow = hoveredCard.name.split(' // ')[0];
        } else {
          nameToShow = hoveredCard.name;
        }

        overlayService.broadcastCard({
          name: nameToShow,
          imageUrl: imageToShow,
          cmc: hoveredCard.cmc,
          type: hoveredCard.type,
        });
      } else {
        overlayService.broadcastCard(null);
      }
    });
  }, [hoveredCard]);

  // Probability settings for inline display (Commander multiplayer defaults to "On the Draw")
  const isCommanderFormat = (fmt?: string) => {
    const normalized = (fmt || '').trim().toLowerCase();
    return normalized.includes('commander') || normalized === 'edh';
  };
  const [onThePlay, setOnThePlay] = useState(() => !isCommanderFormat(format)); // true = play, false = draw
  const prevFormatRef = useRef(format);
  useEffect(() => {
    const wasCommander = isCommanderFormat(prevFormatRef.current);
    const nowCommander = isCommanderFormat(format);
    if (nowCommander && !wasCommander) {
      setOnThePlay(false);
    } else if (!nowCommander && wasCommander) {
      setOnThePlay(true);
    }
    prevFormatRef.current = format;
  }, [format]);
  const [probTargetTurn, setProbTargetTurn] = useState(3); // Default to turn 3
  const [extraDraws, setExtraDraws] = useState(0); // Cantrips, etc.
  const [selectedTurn, setSelectedTurn] = useState(3); // Turn selector for Hand Probability
  const [selectedMinLands, setSelectedMinLands] = useState(3); // Minimum lands threshold for Any Land
  const [anyLandTurn, setAnyLandTurn] = useState(3); // Independent turn for Any Land section
  const [anyLandHandSize, setAnyLandHandSize] = useState(7); // Independent hand size for Any Land section
  const [startingHandSize, setStartingHandSize] = useState(7); // Mulligan to X - starting hand size (7, 6, 5, 4)
  const [selectedCardForProb, setSelectedCardForProb] = useState<string | null>(null); // Card to calculate probability for
  const [cardProbMinCopies, setCardProbMinCopies] = useState(1); // Minimum copies wanted
  const [cardProbTurn, setCardProbTurn] = useState(1); // Turn for card probability (separate from land)
  const [cardProbPlan, setCardProbPlan] = useState<'base' | 'A' | 'B' | 'C' | 'D' | 'E'>('base'); // Which plan to use for copy counting
  const [selectedLandForProb, setSelectedLandForProb] = useState<string | null>(null); // Specific land to calculate probability for
  const [landProbMinCopies, setLandProbMinCopies] = useState(1); // Min copies of specific land wanted
  // Additional card/land slots for combined probability
  const [extraCardSlots, setExtraCardSlots] = useState<{ id: string | null, minCopies: number }[]>([]);
  const [extraLandSlots, setExtraLandSlots] = useState<{ id: string | null, minCopies: number }[]>([]);
  // Bottom cards after mulligan - cards known to be on bottom of library
  // Array of card IDs, one for each card below 7 (e.g., mull to 5 = 2 bottom slots)
  const [bottomedCards, setBottomedCards] = useState<(string | null)[]>([]);

  // Collapsible section states for Probabilities tab
  const [curveExpanded, setCurveExpanded] = useState(true);
  const [calcExpanded, setCalcExpanded] = useState(true);
  const [handCalcExpanded, setHandCalcExpanded] = useState(false);
  const [landsExpanded, setLandsExpanded] = useState(true);
  const [sideboardExpanded, setSideboardExpanded] = useState(true);
  const [extraExpanded, setExtraExpanded] = useState(true);

  // Plan popup state - for editing per-copy costs and sideboard status for a single plan
  const [planPopup, setPlanPopup] = useState<{
    card: Card;
    plan: 'A' | 'B' | 'C' | 'D' | 'E';
    mvs: number[];
    sbs: boolean[]; // true = sideboarded (for mainboard cards) or mainboarded (for sideboard cards)
  } | null>(null);

  // BUILD tab state - with localStorage persistence
  const [buildTargetDeckSize, setBuildTargetDeckSize] = useState<number | null>(() => {
    const saved = localStorage.getItem('coast_targetDeckSize');
    return saved ? parseInt(saved) : null;
  });
  // Per-match land target settings (like ratios — each plan can have its own override)
  const [buildTargetLandsMap, setBuildTargetLandsMap] = useState<Record<string, number | null>>(() => {
    const saved = localStorage.getItem('coast_targetLandsMap');
    if (saved) {
      try { return JSON.parse(saved); } catch { /* fall through */ }
    }
    // Migrate from old single value if exists
    const oldLands = localStorage.getItem('coast_targetLands');
    const val = oldLands ? parseInt(oldLands) : null;
    return { base: val, A: null, B: null, C: null, D: null, E: null };
  });
  // Helper to get current plan's land target (null = snap to actual)
  const buildTargetLands = buildTargetLandsMap[activePlan] ?? null;
  const setBuildTargetLands = (value: number | null) => {
    setBuildTargetLandsMap(prev => ({ ...prev, [activePlan]: value }));
  };
  // Per-match ratio settings (null = snap to actual WPL)
  const [buildTargetRatios, setBuildTargetRatios] = useState<Record<string, number | null>>(() => {
    const saved = localStorage.getItem('coast_targetRatiosV2');
    if (saved) {
      try { return JSON.parse(saved); } catch { /* fall through */ }
    }
    // Migrate from old format: check if old ratios exist (non-null numbers)
    const oldSaved = localStorage.getItem('coast_targetRatios');
    if (oldSaved) {
      try {
        const oldRatios = JSON.parse(oldSaved);
        // Keep base override if it was set, set plans to null (snap)
        return { base: oldRatios.base ?? null, A: null, B: null, C: null, D: null, E: null };
      } catch { /* fall through */ }
    }
    return { base: null, A: null, B: null, C: null, D: null, E: null };
  });
  // Helper to get current plan's ratio (null = snap to actual)
  const buildTargetRatio = buildTargetRatios[activePlan] ?? null;
  const setBuildTargetRatio = (value: number | null) => {
    setBuildTargetRatios(prev => ({ ...prev, [activePlan]: value }));
  };
  const [buildSearchMV, setBuildSearchMV] = useState<number | null>(null); // MV slot to add cards to
  // MV parity filter for companions: 'all' | 'odds' (Obosh) | 'evens' (Gyruda)
  const [mvParity, setMvParity] = useState<'all' | 'odds' | 'evens'>('all');
  // Exclude MV 0 from anchor suggestions (most decks don't want 0-drops)
  const [excludeZero, setExcludeZero] = useState<boolean>(false);

  // Auto-Suggest Lands state (default off)
  const [autoSuggestLands, setAutoSuggestLands] = useState(false);

  // Compute Karsten Land Suggestions and dynamic advice
  const manaAdvice = useMemo(() => {
    const targetL = buildTargetLands ?? 24;
    return generateLandSuggestions(deck, targetL, format || 'standard');
  }, [deck, buildTargetLands, format]);

  const handleAutoSuggestLands = useCallback(() => {
    if (!onAddCard) return;

    const targetL = buildTargetLands ?? 24;
    const result = generateMissingLandSuggestions(deck, targetL, format || 'standard');
    if (!result.suggestedLands || result.suggestedLands.length === 0) return;

    // Apply suggested basic land quantities to deck
    for (const land of result.suggestedLands) {
      const existingCard = deck.find(c => c.name.toLowerCase() === land.name.toLowerCase() && c.isLand);
      if (existingCard && onUpdateCard) {
        onUpdateCard(existingCard.id, { quantity: land.quantity });
      } else {
        const newLandCard: Card = {
          id: `suggested-land-${land.name.toLowerCase()}-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
          name: land.name,
          quantity: land.quantity,
          type: land.type,
          manaCost: '',
          cmc: 0,
          colors: land.colors,
          imageUrl: land.imageUrl || '',
          scryfallId: land.name,
          isLand: true,
        };
        onAddCard(newLandCard);
      }
    }

    // Remove basic lands no longer needed in rebalanced suite
    const suggestedNames = result.suggestedLands.map(l => l.name.toLowerCase());
    const basicNames = ['plains', 'island', 'swamp', 'mountain', 'forest'];
    deck.filter(c => c.isLand && basicNames.includes(c.name.toLowerCase())).forEach(existingLand => {
      if (!suggestedNames.includes(existingLand.name.toLowerCase()) && onRemoveCard) {
        onRemoveCard(existingLand.id);
      }
    });
  }, [deck, buildTargetLands, format, onAddCard, onUpdateCard, onRemoveCard]);

  // Automatically sync/suggest lands whenever autoSuggestLands toggle is active
  useEffect(() => {
    if (autoSuggestLands) {
      handleAutoSuggestLands();
    }
  }, [autoSuggestLands, handleAutoSuggestLands]);
  // Max MV cap - when set, only MVs 0 to this value are shown and distribution is capped
  const [maxMVCap, setMaxMVCap] = useState<number | null>(null);
  // Min MV floor - when set, only MVs from this value up are shown (hides lower MVs)
  const [minMVFloor, setMinMVFloor] = useState<number | null>(null);
  // Per-MV column lock state for Dynamic #Drop columns
  const [lockedMVs, setLockedMVs] = useState<number[]>([]);
  const toggleLockMV = useCallback((mv: number) => {
    setLockedMVs((prev) => (prev.includes(mv) ? prev.filter((v) => v !== mv) : [...prev, mv]));
  }, []);

  // Badge tooltip portal state
  const [badgeTooltip, setBadgeTooltip] = useState<{
    rect: DOMRect;
    content: React.ReactNode;
  } | null>(null);
  const badgeTooltipTimer = useRef<ReturnType<typeof setTimeout>>();
  const showBadgeTooltip = useCallback((e: React.MouseEvent | React.FocusEvent, content: React.ReactNode) => {
    clearTimeout(badgeTooltipTimer.current);
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setBadgeTooltip({ rect, content });
  }, []);
  const hideBadgeTooltip = useCallback(() => {
    badgeTooltipTimer.current = setTimeout(() => setBadgeTooltip(null), 120);
  }, []);

  // Save toolbar settings to localStorage when they change
  useEffect(() => {
    if (buildTargetDeckSize !== null) {
      localStorage.setItem('coast_targetDeckSize', String(buildTargetDeckSize));
    }
  }, [buildTargetDeckSize]);
  useEffect(() => {
    localStorage.setItem('coast_targetLandsMap', JSON.stringify(buildTargetLandsMap));
  }, [buildTargetLandsMap]);
  useEffect(() => {
    localStorage.setItem('coast_targetRatiosV2', JSON.stringify(buildTargetRatios));
  }, [buildTargetRatios]);

  // Sync hand sizes with Play/Draw toggle
  useEffect(() => {
    const defaultHand = onThePlay ? 7 : 8;
    setAnyLandHandSize(defaultHand);
    setStartingHandSize(defaultHand);
  }, [onThePlay]);

  // Auto-detect deck size anchor on mount (when deck is loaded from storage)
  useEffect(() => {
    if (buildTargetDeckSize === null && deck.length > 0) {
      // Calculate total deck size including mainboard
      const mainboardCount = deck.filter(c => !c.isExtraCard).reduce((s, c) => s + c.quantity, 0);
      // Check for commanders to determine if it's a commander deck
      const hasCommander = sideboard.some(c => c.isCommander);

      // Determine closest anchor
      let detectedSize: number;
      if (hasCommander || mainboardCount >= 95) {
        detectedSize = 99;
      } else if (mainboardCount >= 75) {
        detectedSize = 80;
      } else if (mainboardCount >= 50) {
        detectedSize = 60;
      } else {
        detectedSize = 40;
      }

      setBuildTargetDeckSize(detectedSize);
    }
  }, [deck.length]); // Only run when deck changes from 0 to something

  // Reset floor and peak caps when ratio or lands change (curve changes)
  useEffect(() => {
    setMaxMVCap(null);
    setMinMVFloor(null);
  }, [buildTargetRatio, buildTargetLands, activePlan]);

  // BUILD computed values are calculated after displayLandCount is defined (see below)

  // Ref for snapshot capture
  const snapshotRef = useRef<HTMLDivElement>(null);

  // Capture snapshot
  const captureSnapshot = async () => {
    if (!snapshotRef.current) return;

    try {
      const canvas = await html2canvas(snapshotRef.current, {
        backgroundColor: '#0a0f1a',
        scale: 2,
      });
      const link = document.createElement('a');
      link.download = `deck-${activePlan === 'base' ? 'base' : `plan-${activePlan}`}-snapshot.png`;
      link.href = canvas.toDataURL();
      link.click();
    } catch (err) {
      console.error('Failed to create snapshot', err);
      alert('Failed to create snapshot');
    }
  };

  // Dynamic plan colors based on deck's color identity
  const planColors = useMemo(() => getDynamicPlanColors(deck), [deck]);

  // Commander color identity validation
  const commanderIdentity = useMemo(() => {
    const commanders = sideboard.filter(c => c.isCommander);
    if (commanders.length === 0) return null;

    // Collect all colors from commanders (color identity)
    const colors = new Set<string>();
    commanders.forEach(c => {
      // Use colorIdentity if available, otherwise fall back to colors
      const cardColors = c.colorIdentity || c.colors || [];
      cardColors.forEach((color: string) => colors.add(color));
    });
    return colors;
  }, [sideboard]);

  // Check if a card is legal for the commander's color identity
  const isCardLegalForCommander = (card: Card): boolean => {
    if (!commanderIdentity) return true; // No commander = all cards legal
    if (card.isLand) return true; // Lands are always legal (basic lands, etc.)
    if (card.isCommander) return true; // Commander itself is always legal

    const cardColors = card.colorIdentity || card.colors || [];
    // Card is legal if ALL its colors are in commander's identity
    // Colorless cards (empty array) are always legal
    return cardColors.every(color => commanderIdentity.has(color));
  };

  // Get all unique plans from deck
  const allPlans = Array.from(new Set(deck.flatMap(c => c.plans || []))).sort();

  // Get display quantities for a plan
  // For base: use card.quantity and card.isSideboard
  // For plans: use planCopySideboard to determine main vs sideboard
  const getDisplayDeck = () => {
    if (activePlan === 'base') return deck;

    const planKey = activePlan as 'A' | 'B' | 'C' | 'D' | 'E';

    // Combine main and sideboard, applying plan-specific configurations
    const allCards = [...deck, ...sideboard];

    return allCards.map(card => {
      let effectiveQty: number;

      // For unified pool: if sideboard card also exists in mainboard, its planQuantity
      // means "how many STAY in side" (set by unified pool logic), not "go to main"
      // So we should NOT count it toward main deck - the mainboard entry has the correct count
      if (card.isSideboard && deck.some(d => d.name === card.name)) {
        // In unified pool mode, sideboard copies are tracked via mainboard card
        effectiveQty = 0;
      }
      // Mainboard cards or sideboard-only cards
      else if (card.planQuantities?.[planKey] !== undefined) {
        effectiveQty = card.planQuantities[planKey]!;
      }
      // Fallback to planCopySideboard for detailed per-copy control
      else {
        const copySideboard = card.planCopySideboard?.[planKey];
        if (copySideboard && copySideboard.length > 0) {
          // For mainboard cards: count copies where isFlagged is FALSE (not sideboarded)
          // For sideboard cards: count copies where isFlagged is TRUE (mainboarded)
          if (card.isSideboard) {
            effectiveQty = copySideboard.filter(isFlagged => isFlagged).length;
          } else {
            effectiveQty = copySideboard.filter(isFlagged => !isFlagged).length;
          }
        } else {
          // Default: mainboard cards keep quantity, sideboard cards get 0
          effectiveQty = card.isSideboard ? 0 : card.quantity;
        }
      }

      // Get effective MV for plan (using planCopyMVs if available)
      const planMV = card.planMVs?.[activePlan];

      return {
        ...card,
        quantity: effectiveQty,
        effectiveMV: planMV ?? card.effectiveMV
      };
    }).filter(card => card.quantity > 0);
  };

  // Get sideboard for a specific plan (cards marked as sideboard for that plan)
  const getDisplaySideboard = () => {
    if (activePlan === 'base') return sideboard;

    const planKey = activePlan as 'A' | 'B' | 'C' | 'D' | 'E';
    const allCards = [...deck, ...sideboard];

    return allCards.map(card => {
      let sideboardQty: number;

      // First check planQuantities (set by MB/SB buttons)
      if (card.planQuantities?.[planKey] !== undefined) {
        const mainQty = card.planQuantities[planKey]!;
        // For sideboard cards: planQuantities is how many go TO main, so remaining stay in side
        // For mainboard cards: planQuantities is how many STAY in main, so the rest go to side
        if (card.isSideboard) {
          sideboardQty = card.quantity - mainQty; // Original qty minus what went to main
        } else {
          sideboardQty = card.quantity - mainQty; // Original qty minus what stayed in main
        }
        sideboardQty = Math.max(0, sideboardQty);
      }
      // Then check planCopySideboard for detailed per-copy control
      else {
        const copySideboard = card.planCopySideboard?.[planKey];
        if (copySideboard && copySideboard.length > 0) {
          // For mainboard cards: count copies where isFlagged is TRUE (sideboarded out)
          // For sideboard cards: count copies where isFlagged is FALSE (still in sideboard)
          if (card.isSideboard) {
            sideboardQty = copySideboard.filter(isFlagged => !isFlagged).length;
          } else {
            sideboardQty = copySideboard.filter(isFlagged => isFlagged).length;
          }
        } else {
          // Default: sideboard cards keep quantity, mainboard cards get 0
          sideboardQty = card.isSideboard ? card.quantity : 0;
        }
      }

      return {
        ...card,
        quantity: sideboardQty
      };
    }).filter(card => card.quantity > 0);
  };

  // Helper to consolidate same-name cards (combines quantities)
  const consolidateCards = (cards: Card[]): Card[] => {
    const cardMap = new Map<string, Card>();
    cards.forEach(card => {
      const existing = cardMap.get(card.name);
      if (existing) {
        cardMap.set(card.name, { ...existing, quantity: existing.quantity + card.quantity });
      } else {
        cardMap.set(card.name, { ...card });
      }
    });
    return Array.from(cardMap.values());
  };

  const displayDeck = consolidateCards(getDisplayDeck());
  const displaySideboard = consolidateCards(getDisplaySideboard());

  // Copy deck to clipboard in standard format
  const copyToClipboard = async () => {
    // Helper to get export name - Room cards need full name, other DFCs need front face only
    const getExportName = (card: Card) => {
      const name = card.name;
      // If not a DFC name, return as-is
      if (!name.includes(' // ')) return name;

      // Room cards (Duskmourn) need the full "Front // Back" name for Arena
      const isRoom = card.type?.toLowerCase().includes('room');
      if (isRoom) return name;

      // Other DFCs (Transform, MDFC, Marvel) - use front face only
      return name.split(' // ')[0];
    };

    const lines = displayDeck.map(card => `${card.quantity} ${getExportName(card)}`);
    if (displaySideboard.length > 0) {
      lines.push('');
      lines.push('Sideboard');
      displaySideboard.forEach(card => lines.push(`${card.quantity} ${getExportName(card)}`));
    }
    const text = lines.join('\n');
    await navigator.clipboard.writeText(text);
  };

  const tabs = [
    { id: 'news' as TabType, label: 'NEWS', icon: Newspaper },
    { id: 'events' as TabType, label: 'EVENTS', icon: Calendar },
    { id: 'build' as TabType, label: 'BREW', icon: FlaskConical },
    { id: 'discover' as TabType, label: 'DISCOVER', icon: Compass },
    { id: 'market' as TabType, label: 'MARKET', icon: ShoppingCart },
    { id: 'session' as TabType, label: 'SESSION', icon: Timer },
    { id: 'faq' as TabType, label: 'FAQ', icon: ClipboardList },
  ];

  // Grouped tabs for mobile dropdown navigation
  const tabGroups: TabGroup[] = [
    {
      label: 'EXPLORE',
      icon: Globe,
      tabs: [
        { id: 'news', label: 'NEWS', icon: Newspaper },
        { id: 'events', label: 'EVENTS', icon: Calendar },
        { id: 'discover', label: 'DISCOVER', icon: Compass },
      ],
    },
    {
      label: 'DECK TOOLS',
      icon: Wrench,
      tabs: [
        { id: 'build', label: 'BREW', icon: FlaskConical },
        { id: 'market', label: 'MARKET', icon: ShoppingCart },
      ],
    },
    {
      label: 'LIVE',
      icon: Radio,
      tabs: [
        { id: 'session', label: 'SESSION', icon: Timer },
      ],
    },
    {
      label: 'SUPPORT',
      icon: HelpCircle,
      tabs: [
        { id: 'faq', label: 'FAQ', icon: ClipboardList },
      ],
    },
  ];

  // Group cards by selected criteria
  const groupCards = (cards: Card[]) => {
    if (groupBy === 'none') return [{ name: 'All Cards', cards }];

    // For alphabetical, just return sorted by name
    if (groupBy === 'name') {
      const sorted = [...cards].sort((a, b) => a.name.localeCompare(b.name));
      return [{ name: 'A-Z', cards: sorted }];
    }

    const groups: Record<string, Card[]> = {};
    cards.forEach(card => {
      let key = 'Other';
      if (groupBy === 'type') {
        // Commanders get their own section at the top
        if (card.isCommander) {
          key = 'Commander';
        } else {
          key = isEffectivelyLand(card) ? 'Lands' :
            card.type.includes('Creature') ? 'Creatures' :
              card.type.includes('Instant') ? 'Instants' :
                card.type.includes('Sorcery') ? 'Sorceries' :
                  card.type.includes('Enchantment') ? 'Enchantments' :
                    card.type.includes('Artifact') ? 'Artifacts' :
                      card.type.includes('Planeswalker') ? 'Planeswalkers' : 'Other';
        }
      } else if (groupBy === 'mv') {
        // Lands get their own group at the bottom, not with MV 0
        if (isEffectivelyLand(card)) {
          key = 'Lands';
        } else {
          const mv = card.effectiveMV ?? card.cmc;
          key = mv >= 6 ? '6+' : String(mv); // Use numeric string for proper sorting
        }
      } else if (groupBy === 'color') {
        if (card.colors.length === 0) key = 'Colorless';
        else if (card.colors.length > 1) key = 'Multicolor';
        else key = { W: 'White', U: 'Blue', B: 'Black', R: 'Red', G: 'Green' }[card.colors[0]] || 'Other';
      }
      if (!groups[key]) groups[key] = [];
      groups[key].push(card);
    });

    // Sort groups - MV numerically ascending, type by priority, others alphabetically
    let entries = Object.entries(groups);
    if (groupBy === 'mv') {
      entries = entries.sort((a, b) => {
        // Lands always at the bottom
        if (a[0] === 'Lands') return 1;
        if (b[0] === 'Lands') return -1;
        const aNum = a[0] === '6+' ? 6 : parseInt(a[0]);
        const bNum = b[0] === '6+' ? 6 : parseInt(b[0]);
        return aNum - bNum;
      });
      // Add "MV" prefix for non-lands
      return entries.map(([key, cards]) => ({
        name: key === 'Lands' ? 'Lands' : key === '6+' ? '6+' : `MV ${key}`,
        cards
      }));
    }

    // Type ordering - Commander first, then standard order
    if (groupBy === 'type') {
      const typeOrder = ['Commander', 'Creatures', 'Planeswalkers', 'Instants', 'Sorceries', 'Enchantments', 'Artifacts', 'Lands', 'Other'];
      entries = entries.sort((a, b) => {
        const aIndex = typeOrder.indexOf(a[0]);
        const bIndex = typeOrder.indexOf(b[0]);
        return (aIndex === -1 ? 99 : aIndex) - (bIndex === -1 ? 99 : bIndex);
      });
    }

    return entries.map(([name, cards]) => ({ name, cards }));
  };

  // Calculate key stats for the stats bar - uses displayDeck which is plan-aware
  // Exclude "Other" cards (tokens, emblems, planes, schemes) from main deck counts
  const displayDeckCards = displayDeck.filter(c => !c.isExtraCard).reduce((sum, c) => sum + c.quantity, 0);

  // Land count: includes regular lands + flipped MDFCs with land backs + plan MDFC copies
  const displayLandCount = useMemo(() => {
    let landCount = displayDeck.filter(c => isEffectivelyLand(c) && !c.isExtraCard).reduce((sum, c) => sum + c.quantity, 0);

    // In match plans, count MDFC copies using land back faces (modal_dfc only, not transform)
    if (activePlan !== 'base') {
      const planKey = activePlan as 'A' | 'B' | 'C' | 'D' | 'E';
      displayDeck.forEach(card => {
        if (card.layout === 'modal_dfc' && card.backType?.toLowerCase().includes('land') && !card.isLand) {
          const copyFaces = card.planCopyFace?.[planKey] || [];
          for (let i = 0; i < card.quantity; i++) {
            if (copyFaces[i] === 1) {
              landCount++; // This copy is treated as a land
            }
          }
        }
      });
    }

    return landCount;
  }, [displayDeck, activePlan]);

  // Spell count: excludes lands AND MDFC copies using land back faces (modal_dfc only)
  const displaySpellCount = useMemo(() => {
    let spellCount = 0;
    displayDeck.forEach(card => {
      if (isEffectivelyLand(card) || card.isExtraCard) return;

      if (activePlan !== 'base' && card.layout === 'modal_dfc' && card.backType?.toLowerCase().includes('land')) {
        // For MDFCs with land backs in match plans, count only front-face copies
        const planKey = activePlan as 'A' | 'B' | 'C' | 'D' | 'E';
        const copyFaces = card.planCopyFace?.[planKey] || [];
        for (let i = 0; i < card.quantity; i++) {
          if (copyFaces[i] !== 1) {
            spellCount++; // This copy is a spell
          }
        }
      } else {
        spellCount += card.quantity;
      }
    });
    return spellCount;
  }, [displayDeck, activePlan]);

  // Virtual land count: includes transform cards marked as will-become-land
  // This is for manabase planning - "mid-game, my deck will have this many land sources"
  const virtualLandCount = useMemo(() => {
    let count = 0;

    // In base mode: count flipped transform cards that become lands
    displayDeck.forEach(card => {
      if (isTransformLandSource(card)) {
        count += card.quantity;
      }
    });

    // In match plans: also count transform cards with planCopyFace marked
    if (activePlan !== 'base') {
      const planKey = activePlan as 'A' | 'B' | 'C' | 'D' | 'E';
      displayDeck.forEach(card => {
        // Skip if already counted as flipped
        if (isTransformLandSource(card)) return;

        if (card.layout === 'transform' && card.backType?.toLowerCase().includes('land') && !card.isLand) {
          const copyFaces = card.planCopyFace?.[planKey] || [];
          for (let i = 0; i < card.quantity; i++) {
            if (copyFaces[i] === 1) {
              count++; // Transform card marked as will-become-land
            }
          }
        }
      });
    }
    return count;
  }, [displayDeck, activePlan]);

  // Calculate total MV - for matches, use per-copy MVs from planCopyMVs
  const displayTotalMV = useMemo(() => {
    if (activePlan === 'base') {
      // Base mode: effectiveMV * quantity
      return displayDeck.filter(c => !isEffectivelyLand(c) && !c.isExtraCard).reduce((sum, c) => sum + (c.effectiveMV ?? c.cmc) * c.quantity, 0);
    }

    // Match mode: sum individual copy MVs
    const planKey = activePlan as 'A' | 'B' | 'C' | 'D' | 'E';
    let totalWeight = 0;

    displayDeck.forEach(card => {
      if (isEffectivelyLand(card) || card.isExtraCard) return;

      const baseMV = card.effectiveMV ?? card.cmc;
      const copyMVs = card.planCopyMVs?.[planKey];
      const copySB = card.planCopySideboard?.[planKey] || [];

      // Sum MVs for copies in main deck (not sideboarded)
      for (let i = 0; i < card.quantity; i++) {
        if (!copySB[i]) { // Not sideboarded
          totalWeight += copyMVs?.[i] ?? baseMV;
        }
      }
    });

    return totalWeight;
  }, [displayDeck, activePlan]);
  const displayAvgMV = displaySpellCount > 0 ? displayTotalMV / displaySpellCount : 0;
  const displayWPL = displayLandCount > 0 ? displayTotalMV / displayLandCount : 0;

  // Aggregate ramp sources and cantrips from classified card data
  // Stores per-card entries so we can filter by MV threshold at render time
  const deckManaProfile = useMemo(() => {
    // Individual ramp entries: { cardMV, manaOut, copies }
    const rampEntries: { cardMV: number; manaOut: number; copies: number }[] = [];
    // Individual draw entries: { cardMV, cardsDrawn, copies }
    const drawEntries: { cardMV: number; cardsDrawn: number; copies: number }[] = [];

    let totalRamp = 0;
    let totalRampMana = 0;
    let totalDraw = 0;
    let totalExtraDraws = 0;

    displayDeck.forEach(card => {
      if (isEffectivelyLand(card) || card.isExtraCard) return;
      const qty = card.quantity;
      const cardMV = card.effectiveMV ?? card.cmc;

      // Ramp sources (R1-R5, exclude R6 timing modifiers)
      if (card.rampCategory && card.rampCategory !== 'none' && card.rampCategory !== 'R6') {
        const manaOut = card.manaProduced || 1;
        rampEntries.push({ cardMV, manaOut, copies: qty });
        totalRamp += qty;
        totalRampMana += manaOut * qty;
      }

      // Cantrips/draw spells
      if (card.drawCategory && card.drawCategory !== 'none') {
        const drawn = card.cardsDrawn || 1;
        drawEntries.push({ cardMV, cardsDrawn: drawn, copies: qty });
        totalDraw += qty;
        totalExtraDraws += drawn * qty;
      }
    });

    // How many effective extra "lands" from ramp castable before targetMV?
    // Only ramp with MV < targetMV can be online in time
    const rampForMV = (targetMV: number): number => {
      let count = 0;
      for (const entry of rampEntries) {
        if (entry.cardMV < targetMV) {
          count += entry.copies;
        }
      }
      return count;
    };

    // How many extra cards seen from draw spells castable before targetMV?
    const drawForMV = (targetMV: number): number => {
      let extra = 0;
      for (const entry of drawEntries) {
        if (entry.cardMV < targetMV) {
          extra += entry.cardsDrawn * entry.copies;
        }
      }
      return extra;
    };

    return {
      rampForMV, drawForMV,
      totalRamp, totalRampMana,
      totalDraw, totalExtraDraws,
    };
  }, [displayDeck]);

  // Calculate plan-specific stats (for actual/plan/target display)
  const planStats = useMemo(() => {
    const allCards = [...deck, ...sideboard];

    // Get active plan key (or null for base)
    const planKey = activePlan !== 'base' ? activePlan as 'A' | 'B' | 'C' | 'D' | 'E' : null;

    let planMainboard = 0;
    let planLands = 0;
    let planSpells = 0;
    let planWeight = 0;
    let planSideboard = 0;

    // For both base and plans, use display values since displayDeck/displaySideboard 
    // already have plan-aware quantities (via planQuantities from MB/SB buttons)
    planMainboard = displayDeckCards;
    planLands = displayLandCount;
    planSpells = displaySpellCount;
    planWeight = displayTotalMV;
    planSideboard = displaySideboard.filter(c => !c.isExtraCard).reduce((s, c) => s + c.quantity, 0);

    return {
      mainboard: planMainboard,
      lands: planLands,
      spells: planSpells,
      weight: planWeight,
      curve: planSpells > 0 ? planWeight / planSpells : 0,
      ratio: planLands > 0 ? planWeight / planLands : 0,
      sideboard: planSideboard,
    };
  }, [deck, sideboard, activePlan, displayDeckCards, displayLandCount, displaySpellCount, displayTotalMV, displaySideboard]);

  // "Other" cards count (tokens, emblems, planes, schemes, etc.) - separate from Main/Side
  const displayOtherCount = [...deck, ...sideboard].filter(c => c.isExtraCard).reduce((sum, c) => sum + c.quantity, 0);

  // Calculate deck legality issues (for global warning indicator)
  const deckLegalityIssues = useMemo(() => {
    const issues: string[] = [];
    const commanders = sideboard.filter(c => c.isCommander);

    if (commanders.length > 0) {
      const backgrounds = commanders.filter(c => c.isBackground);
      const nonBGCommanders = commanders.filter(c => !c.isBackground);
      const partnersCount = nonBGCommanders.filter(c => c.hasPartner || c.hasPartnerWith).length;
      const chooseBGCount = nonBGCommanders.filter(c => c.choosesBackground).length;
      const regularCommanders = nonBGCommanders.filter(c => !c.hasPartner && !c.hasPartnerWith && !c.choosesBackground);

      if (nonBGCommanders.length > 2) {
        issues.push('Too many commanders');
      }
      if (nonBGCommanders.length === 2 && partnersCount < 2 && backgrounds.length === 0) {
        issues.push('Commander without Partner');
      }
      if (regularCommanders.length > 0 && (partnersCount > 0 || backgrounds.length > 0)) {
        issues.push('Invalid commander combination');
      }
      if (backgrounds.length > 0 && chooseBGCount === 0) {
        issues.push('Background needs "Choose a Background" commander');
      }
      if (backgrounds.length > 1) {
        issues.push('Too many Backgrounds');
      }
    }

    return issues;
  }, [sideboard]);

  // BUILD tab computed values (after displayLandCount is available)
  const formatDeckSize = isCommanderFormat(format) ? 99 : format === 'Limited 40' ? 40 : 60;
  const targetDeckSize = buildTargetDeckSize ?? formatDeckSize;
  // SNAP LOGIC: When user hasn't set a per-plan override, snap to actual land count
  // User inputs override, but otherwise the anchor follows the deck composition
  const effectiveTargetLands = buildTargetLands ?? (displayLandCount || (targetDeckSize === 99 ? 36 : targetDeckSize === 40 ? 17 : 24));
  const targetNonlands = targetDeckSize - effectiveTargetLands;
  // SNAP LOGIC for ratio: When no override, snap to actual WPL (weight per land)
  // Default to 3 if no deck data yet
  const effectiveTargetRatio = buildTargetRatio ?? (displayWPL > 0 ? displayWPL : 3);
  const targetDeckWeight = Math.round(effectiveTargetLands * effectiveTargetRatio);
  const targetManaCurve = targetNonlands > 0 ? targetDeckWeight / targetNonlands : 0;

  // Anchor distribution - ideal cards per MV slot based on target curve
  // DECKSTR ALGORITHM: Total Mana Weight = Lands × Ratio
  // Σ(MV × CardCount) = Total Mana Weight
  const anchorDistribution = useMemo(() => {
    // Calculate max MV in deck for dynamic extension
    const deckMaxMV = displayDeck.reduce((max, card) => {
      if (isEffectivelyLand(card) || card.isExtraCard) return max;
      const baseMV = card.effectiveMV ?? card.cmc;
      let cardMax = baseMV;
      if (activePlan !== 'base') {
        const planKey = activePlan as 'A' | 'B' | 'C' | 'D' | 'E';
        const copyMVs = card.planCopyMVs?.[planKey];
        if (copyMVs && copyMVs.length > 0) {
          cardMax = Math.max(cardMax, ...copyMVs);
        }
      }
      return Math.max(max, cardMax);
    }, 0);

    const curveBasedMax = Math.ceil(targetManaCurve) + 5;
    const maxMV = maxMVCap !== null ? maxMVCap : Math.max(deckMaxMV, curveBasedMax, 6);
    const slots = Array.from({ length: maxMV + 1 }, (_, i) => i);

    // Filter for valid slots
    const isValidSlot = (mv: number) => {
      if (minMVFloor !== null && mv < minMVFloor) return false;
      if (excludeZero && mv === 0) return false;
      if (mvParity === 'odds' && mv % 2 === 0) return false;
      if (mvParity === 'evens' && mv % 2 === 1) return false;
      if (mv < 0 || mv >= slots.length) return false;
      return true;
    };

    // ================================================================
    // DECKSTR ANCHOR ALGORITHM - Deterministic Strict Bell Generator
    // 
    // Inputs: S (nonlands), W (target weight), anchor mode (with-0 / no-0)
    // Output: Unique integer distribution satisfying:
    //   Σn_i = S
    //   Σ(i × n_i) = W
    //   n_m < n_(m+1) < ... < n_p > n_(p+1) > ... > n_M (strict bell)
    // ================================================================

    const result = slots.map(() => 0);
    if (targetNonlands <= 0) return result;

    const S = targetNonlands;
    const W = targetDeckWeight;
    const c = W / S;  // curve (average MV)

    // ================================================================
    // 1) Peak: p = round(c) - aligns with Gaussian center
    // For half-values, round toward the higher weight side
    // ================================================================
    const p = Math.max(1, Math.round(c));

    // ================================================================
    // 2) PHASE 1: Compute BASE distribution using FULL range
    //    (ignoring floor/peak constraints — those are applied after)
    // ================================================================
    const with0s = !excludeZero;  // Phase 1 always includes MV 0 unless excludeZero — floor is applied in Phase 2
    const baseM = with0s ? 0 : 1;  // base lower bin (always start from 0 or 1)

    // Upper bin: start at p+2, expand if needed, cap at p+6
    let baseUpper = p + 2;
    const maxUpper = p + 6;

    while (baseUpper < maxUpper && c > baseUpper) {
      baseUpper++;
    }
    baseUpper = Math.min(baseUpper, maxMV);

    // ================================================================
    // 3) Generate Gaussian weights centered at c (full range)
    // ================================================================
    const sigma = 1.0;  // fixed spread
    const weights: number[] = [];

    for (let i = baseM; i <= baseUpper; i++) {
      // Check parity validity (but NOT floor/peak — those are post-processed)
      const parityValid = (mvParity === 'all' ||
        (mvParity === 'odds' && i % 2 === 1) ||
        (mvParity === 'evens' && i % 2 === 0)) &&
        !(excludeZero && i === 0) &&
        i >= 0 && i < slots.length;

      if (!parityValid) {
        weights.push(0);
      } else {
        // w_i = exp(-(i - c)² / (2σ²))
        const w = Math.exp(-Math.pow(i - c, 2) / (2 * sigma * sigma));
        weights.push(w);
      }
    }

    // Normalize and scale to S
    const totalWeight = weights.reduce((a, b) => a + b, 0);
    const r: number[] = weights.map(w => (totalWeight > 0 ? (w / totalWeight) * S : 0));

    // ================================================================
    // 4) Largest Remainder rounding (Hamilton method)
    // ================================================================
    const n: number[] = r.map(x => Math.floor(x));
    let distributed = n.reduce((a, b) => a + b, 0);
    const R = S - distributed;

    // Get fractional parts with indices
    const fractionals = r.map((x, i) => ({ index: i, frac: x - Math.floor(x) }));
    fractionals.sort((a, b) => b.frac - a.frac);

    // Add +1 to the R bins with largest fractional parts
    for (let k = 0; k < R && k < fractionals.length; k++) {
      n[fractionals[k].index]++;
    }

    // ================================================================
    // 5) Weight tuning: adjust to hit exact W on BASE distribution
    // ================================================================
    let currentW = n.reduce((sum, count, i) => sum + (baseM + i) * count, 0);
    const maxIterations = S * 10;
    let iterations = 0;

    while (currentW !== W && iterations < maxIterations) {
      iterations++;

      if (currentW < W) {
        let moved = false;
        for (let i = 0; i < n.length - 1; i++) {
          if (n[i] > 0) {
            n[i]--;
            n[i + 1]++;
            currentW++;
            moved = true;
            break;
          }
        }
        if (!moved) break;
      } else {
        let moved = false;
        for (let i = n.length - 1; i > 0; i--) {
          if (n[i] > 0) {
            n[i]--;
            n[i - 1]++;
            currentW--;
            moved = true;
            break;
          }
        }
        if (!moved) break;
      }
    }

    if (currentW !== W) {
      console.warn('DECKSTR: Weight target not achieved', { currentW, W, c, S, n });
    }

    // ================================================================
    // 6) Apply base distribution to result array
    // ================================================================
    for (let i = 0; i < n.length; i++) {
      const mv = baseM + i;
      if (mv < result.length) {
        result[mv] = n[i];
      }
    }

    // Verify count
    const totalCount = result.reduce((a, b) => a + b, 0);
    if (totalCount !== S) {
      const peakMV = p;
      if (peakMV < result.length) {
        result[peakMV] += (S - totalCount);
        if (result[peakMV] < 0) result[peakMV] = 0;
      }
    }

    // ================================================================
    // 7) PHASE 2: Apply floor/peak constraints by folding edge copies
    //    This LOCKS interior slots — only boundary slots change
    // ================================================================

    // Floor: fold all copies below minMVFloor into the floor slot
    if (minMVFloor !== null && minMVFloor > 0) {
      let belowFloorCopies = 0;
      for (let i = 0; i < minMVFloor && i < result.length; i++) {
        belowFloorCopies += result[i];
        result[i] = 0;
      }
      if (minMVFloor < result.length) {
        result[minMVFloor] += belowFloorCopies;
      }
    }

    // Peak cap: fold all copies above maxMVCap into the cap slot
    if (maxMVCap !== null && maxMVCap < result.length) {
      let aboveCapCopies = 0;
      for (let i = maxMVCap + 1; i < result.length; i++) {
        aboveCapCopies += result[i];
        result[i] = 0;
      }
      result[maxMVCap] += aboveCapCopies;
    }

    // ================================================================
    // 8) PHASE 3: Weight integrity after folding
    //    Folding changes total weight — compensate by cascading shifts
    //    through adjacent interior slots. Peak/floor boundaries are
    //    locked, weight adjustments ripple through the middle.
    // ================================================================
    let postFoldWeight = result.reduce((sum, count, mv) => sum + mv * count, 0);
    const lo = minMVFloor ?? 0;
    const hi = maxMVCap ?? (result.length - 1);
    let weightIters = 0;
    const weightMaxIters = S * 10;

    while (postFoldWeight !== W && weightIters < weightMaxIters) {
      weightIters++;

      if (postFoldWeight > W) {
        // Excess weight (floor fold moved cheap→expensive)
        // Shift 1 card downward from nearest interior slot toward floor
        // Search from nearest to floor first, cascade outward if needed
        // Never touch the peak cap slot (hi)
        let moved = false;
        for (let i = lo + 1; i <= Math.min(hi - 1, result.length - 1); i++) {
          if (result[i] > 0) {
            result[i]--;
            result[i - 1]++;
            postFoldWeight--;
            moved = true;
            break;
          }
        }
        if (!moved) break;
      } else {
        // Deficit weight (peak fold moved expensive→cheaper)
        // Shift 1 card upward from nearest interior slot toward cap
        // Never touch the floor slot (lo)
        let moved = false;
        for (let i = Math.max(lo + 1, 0); i < hi; i++) {
          if (result[i] > 0) {
            result[i]--;
            result[i + 1]++;
            postFoldWeight++;
            moved = true;
            break;
          }
        }
        if (!moved) break;
      }
    }

    return result;
  }, [targetManaCurve, targetNonlands, targetDeckWeight, displayDeck, activePlan, mvParity, excludeZero, maxMVCap, minMVFloor]);

  // WPL status color
  const getWPLColor = (wpl: number) => {
    if (wpl >= 2.5 && wpl <= 3.5) return 'text-green-400';
    if (wpl >= 2 && wpl < 2.5) return 'text-yellow-400';
    if (wpl > 3.5 && wpl <= 4) return 'text-yellow-400';
    return 'text-red-400';
  };

  // Note: mvParity is a VIEW filter only - cards can be added in any mode
  // The filter controls which MV columns are displayed, not what can be added

  return (
    <div className="bg-[#020617] rounded-xl border border-white/10 h-full flex flex-col perspective-container overflow-hidden">
      {/* Badge tooltip portal - renders outside all overflow containers */}
      {badgeTooltip && createPortal(
        <div
          style={{
            position: 'fixed',
            left: badgeTooltip.rect.left + badgeTooltip.rect.width / 2,
            top: badgeTooltip.rect.top - 8,
            transform: 'translate(-50%, -100%)',
            zIndex: 99999,
            width: 'max-content',
            maxWidth: '280px',
            pointerEvents: 'none',
            animation: 'fadeIn 150ms ease-out',
          }}
        >
          {badgeTooltip.content}
          <div style={{
            position: 'absolute',
            left: '50%',
            bottom: '-4px',
            transform: 'translateX(-50%) rotate(45deg)',
            width: '8px',
            height: '8px',
            background: 'rgba(15,23,42,0.97)',
            borderRight: '1px solid rgba(6,182,212,0.3)',
            borderBottom: '1px solid rgba(6,182,212,0.3)',
          }} />
        </div>,
        document.body
      )}
      {/* Tab Navigation — portaled into Header's center area */}
      {navSlot && createPortal(
        <NavDropdown
          groups={tabGroups}
          activeTab={activeTab}
          onTabChange={(id) => setActiveTab(id as TabType)}
        />,
        navSlot
      )}

      {/* Tab Content */}
      <div className="flex-1 p-4 overflow-auto">
        {activeTab === 'build' && (
          <div className="space-y-4">
            {/* Search Container - with import button and advanced search */}
            {onAddCard && (
              <div className="mb-3">
                <CardSearch
                  onAddCard={onAddCard}
                  onAddToSideboard={onAddToSideboard}
                  onShowImport={() => setShowImportModal(true)}
                  stats={{
                    weight: displayTotalMV,
                    targetWeight: Math.round(targetDeckWeight),
                    curve: displayAvgMV,
                    targetCurve: targetManaCurve,
                    actualRatio: displayLandCount > 0 ? displayTotalMV / displayLandCount : 0,
                    nonlands: displaySpellCount,
                    targetNonlands: targetNonlands,
                    lands: displayLandCount,
                    sideboard: displaySideboard.filter(c => !c.isExtraCard).reduce((s, c) => s + c.quantity, 0),
                    mainboard: displayDeckCards,
                    extraCards: displayOtherCount,
                    // Plan-specific stats
                    planStats: planStats,
                  }}
                  toolbar={{
                    targetDeckSize,
                    setTargetDeckSize: setBuildTargetDeckSize,
                    effectiveTargetLands,
                    displayLandCount,
                    setTargetLands: setBuildTargetLands,
                    buildTargetRatio: effectiveTargetRatio,
                    setTargetRatio: setBuildTargetRatio,
                    mvParity,
                    setMvParity,
                    excludeZero,
                    setExcludeZero,
                    groupBy,
                    setGroupBy: (val: string) => setGroupBy(val as GroupBy),
                    activePlan,
                    setActivePlan: (val: string) => setActivePlan(val),
                    viewMode,
                    setViewMode,
                    onThePlay,
                    setOnThePlay,
                    probTargetTurn,
                    setProbTargetTurn,
                    copyToClipboard,
                    saveDeck: () => setShowSaveModal(true),
                    clearDeck: onClearDeck,
                    clearPlan: () => {
                      // Reset all plan-specific data
                      deck.forEach(card => {
                        onUpdateCard?.(card.id, {
                          planQuantities: undefined,
                          planCopyMVs: undefined,
                          planCopySideboard: undefined,
                          planCopyFace: undefined,
                        });
                      });
                    },
                  }}
                  deck={deck}
                  format={format}
                  onFormatChange={(nextFormat) => {
                    if (isCommanderFormat(nextFormat)) {
                      setOnThePlay(false);
                    } else {
                      setOnThePlay(true);
                    }
                    onFormatDetect?.(nextFormat);
                  }}
                  autoSuggestLands={autoSuggestLands}
                  onToggleAutoSuggestLands={setAutoSuggestLands}
                  onAutoSuggestLands={handleAutoSuggestLands}
                />
              </div>
            )}


            {/* Command Zone - Commanders shown FIRST at the top */}
            {sideboard.some(c => c.isCommander) && onCardClick && (
              <div className="bg-amber-500/5 rounded-lg p-2 mb-3 border border-amber-500/20">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-amber-400 text-sm font-medium flex items-center gap-1">
                    Command Zone
                  </span>
                  <span className="text-amber-400/50 text-xs">
                    {sideboard.filter(c => c.isCommander).reduce((sum, c) => sum + c.quantity, 0)} commander(s)
                  </span>
                </div>
                <div className="space-y-0.5">
                  {(() => {
                    // Pre-calculate which commanders are illegal
                    const commanders = sideboard.filter(c => c.isCommander);
                    const backgrounds = commanders.filter(c => c.isBackground);
                    const nonBGCommanders = commanders.filter(c => !c.isBackground);
                    const partners = nonBGCommanders.filter(c => c.hasPartner || c.hasPartnerWith);
                    const chooseBGCards = nonBGCommanders.filter(c => c.choosesBackground);
                    const regularCommanders = nonBGCommanders.filter(c => !c.hasPartner && !c.hasPartnerWith && !c.choosesBackground);

                    // Determine illegal cards
                    const getIllegalReason = (card: Card): string | null => {
                      // Background without Choose a Background commander
                      if (card.isBackground && chooseBGCards.length === 0) {
                        return 'No "Choose a Background" commander';
                      }
                      // Regular commander mixed with partners
                      if (!card.hasPartner && !card.hasPartnerWith && !card.choosesBackground && !card.isBackground) {
                        if (nonBGCommanders.length > 1) {
                          return 'No Partner ability - can\'t share command zone';
                        }
                        if (backgrounds.length > 0) {
                          return 'Can\'t be combined with Background';
                        }
                      }
                      // Too many non-BG commanders
                      if (nonBGCommanders.length > 2 && !card.isBackground) {
                        return 'Too many commanders';
                      }
                      // Partner without another partner (when there's 2+ commanders)
                      if ((card.hasPartner || card.hasPartnerWith) && nonBGCommanders.length >= 2 && partners.length < 2) {
                        return 'Partner needs another Partner commander';
                      }
                      return null;
                    };

                    return commanders.map(card => {
                      const illegalReason = getIllegalReason(card);
                      return (
                        <div
                          key={card.id}
                          className={`flex items-center gap-2 ${illegalReason ? 'bg-red-500/20 border border-red-500/30' : 'bg-amber-500/20'} hover:bg-amber-500/30 rounded px-2 py-1.5 transition-colors`}
                        >
                          {illegalReason && (
                            <span className="text-red-400" title={illegalReason}>⚠️</span>
                          )}
                          <span
                            className={`${illegalReason ? 'text-red-300' : 'text-amber-300'} text-sm font-medium flex-1 cursor-pointer hover:text-amber-100`}
                            onClick={() => onCardClick(card)}
                          >
                            {card.name}
                          </span>
                          {/* Partner/Background badges */}
                          {card.hasPartner && (
                            <span className="px-1.5 py-0.5 text-[9px] bg-purple-500/30 text-purple-300 rounded font-medium" title="Has Partner">
                              Partner
                            </span>
                          )}
                          {card.hasPartnerWith && (
                            <span className="px-1.5 py-0.5 text-[9px] bg-pink-500/30 text-pink-300 rounded font-medium" title={`Partner with ${card.hasPartnerWith}`}>
                              Partner w/
                            </span>
                          )}
                          {card.choosesBackground && (
                            <span className="px-1.5 py-0.5 text-[9px] bg-green-500/30 text-green-300 rounded font-medium" title="Choose a Background">
                              +BG
                            </span>
                          )}
                          {card.isBackground && (
                            <span className="px-1.5 py-0.5 text-[9px] bg-emerald-500/30 text-emerald-300 rounded font-medium" title="Background enchantment">
                              BG
                            </span>
                          )}
                          <span className="text-amber-400/50 text-xs">MV {card.effectiveMV ?? card.cmc}</span>
                          {/* Unassign as commander button */}
                          <button
                            onClick={() => onUpdateSideboardCard?.(card.id, { isCommander: false })}
                            className="px-1.5 py-0.5 text-[10px] text-red-400/60 hover:text-red-400 hover:bg-red-500/20 rounded transition-colors"
                            title="Remove from Command Zone"
                          >
                            ✖
                          </button>
                        </div>
                      );
                    });
                  })()}
                </div>
                {/* Commander legality warnings */}
                {(() => {
                  const commanders = sideboard.filter(c => c.isCommander);
                  const backgrounds = commanders.filter(c => c.isBackground);
                  const nonBGCommanders = commanders.filter(c => !c.isBackground);
                  const partnersCount = nonBGCommanders.filter(c => c.hasPartner || c.hasPartnerWith).length;
                  const chooseBGCount = nonBGCommanders.filter(c => c.choosesBackground).length;
                  const regularCommanders = nonBGCommanders.filter(c => !c.hasPartner && !c.hasPartnerWith && !c.choosesBackground);

                  // Calculate valid configurations
                  const warnings: string[] = [];

                  // Check for too many commanders
                  if (nonBGCommanders.length > 2) {
                    warnings.push(`⚠️ Too many commanders! You have ${nonBGCommanders.length} non-background commanders (max 2 with Partner)`);
                  }

                  // Check for commanders without partner in multi-commander setup
                  if (nonBGCommanders.length === 2 && partnersCount < 2 && backgrounds.length === 0) {
                    const nonPartners = nonBGCommanders.filter(c => !c.hasPartner && !c.hasPartnerWith && !c.choosesBackground);
                    if (nonPartners.length > 0) {
                      warnings.push(`⚠️ ${nonPartners.map(c => c.name).join(', ')} doesn't have Partner - can't be paired with another commander`);
                    }
                  }

                  // Check for Choose a Background without a Background
                  if (chooseBGCount > 0 && backgrounds.length === 0) {
                    warnings.push(`💡 Commander can Choose a Background - add a Background enchantment`);
                  }

                  // Check for Background without Choose a Background commander
                  if (backgrounds.length > 0 && chooseBGCount === 0) {
                    warnings.push(`⚠️ Background requires a commander with "Choose a Background"`);
                  }

                  // Check for too many backgrounds
                  if (backgrounds.length > 1) {
                    warnings.push(`⚠️ Too many Backgrounds! You can only have 1 Background`);
                  }

                  // Check for regular commander mixed with partners
                  if (regularCommanders.length > 0 && (partnersCount > 0 || backgrounds.length > 0)) {
                    warnings.push(`⚠️ ${regularCommanders.map(c => c.name).join(', ')} can't be combined with Partner commanders or Backgrounds`);
                  }

                  // Suggest partner if only 1 partner commander
                  if (nonBGCommanders.length === 1 && partnersCount === 1) {
                    return (
                      <div className="mt-2 text-[10px] text-amber-400/50 italic">
                        💡 This commander has Partner - you may add another Partner commander
                      </div>
                    );
                  }

                  if (warnings.length > 0) {
                    return (
                      <div className="mt-2 space-y-1">
                        {warnings.map((warning, i) => (
                          <div key={i} className={`text-[10px] ${warning.startsWith('⚠️') ? 'text-red-400/90' : 'text-amber-400/60'} italic`}>
                            {warning}
                          </div>
                        ))}
                      </div>
                    );
                  }

                  return null;
                })()}
              </div>
            )}

            {/* Companion Zone - shown below Command Zone */}
            {sideboard.some(c => c.isCompanion) && onCardClick && (
              <div className="bg-purple-500/5 rounded-lg p-2 mb-3 border border-purple-500/20">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-purple-400 text-sm font-medium flex items-center gap-1">
                    Companion
                  </span>
                </div>
                <div className="space-y-0.5">
                  {sideboard.filter(c => c.isCompanion).map(card => (
                    <div
                      key={card.id}
                      className="flex items-center gap-2 bg-purple-500/20 hover:bg-purple-500/30 rounded px-2 py-1.5 transition-colors"
                    >
                      <span
                        className="text-purple-300 text-sm font-medium flex-1 cursor-pointer hover:text-purple-100"
                        onClick={() => onCardClick(card)}
                      >
                        {card.name}
                      </span>
                      {card.companionCondition && (
                        <span className="text-[8px] text-purple-400/60 max-w-[150px] truncate" title={card.companionCondition}>
                          {card.companionCondition}
                        </span>
                      )}
                      <span className="text-purple-400/50 text-xs">MV {card.effectiveMV ?? card.cmc}</span>
                      {/* Move to Main Deck button */}
                      {onMoveToMainDeck && (
                        <button
                          onClick={() => onMoveToMainDeck(card.id)}
                          className="px-1.5 py-0.5 text-[10px] text-blue-400/60 hover:text-blue-400 hover:bg-blue-500/20 rounded transition-colors"
                          title="Move to Main Deck"
                        >
                          →MB
                        </button>
                      )}
                      {/* Remove from sideboard button */}
                      {onRemoveFromSideboard && (
                        <button
                          onClick={() => onRemoveFromSideboard(card.id)}
                          className="px-1.5 py-0.5 text-[10px] text-red-400/60 hover:text-red-400 hover:bg-red-500/20 rounded transition-colors"
                          title="Remove from deck"
                        >
                          ✖
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Main Deck - With Match Applied (List/Grid views only) */}
            {viewMode !== 'dynamic' && displayDeck.length > 0 && onCardClick && onUpdateCard && onRemoveCard && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white font-medium text-sm">
                    Main Deck {activePlan !== 'base' && <span className="text-cyan-400">(Match {activePlan === 'A' ? '1' : activePlan === 'B' ? '2' : activePlan === 'C' ? '3' : activePlan === 'D' ? '4' : '5'})</span>}
                  </span>
                  <span className="text-white/60 text-sm">
                    {displayDeck.reduce((s, c) => s + c.quantity, 0)} cards
                    {activePlan !== 'base' && displayDeck.reduce((s, c) => s + c.quantity, 0) !== totalCards && (
                      <span className="text-amber-400 ml-1">(base: {totalCards})</span>
                    )}
                  </span>
                </div>
                {viewMode === 'grid' ? (
                  <DeckGrid
                    groups={groupCards(displayDeck)}
                    onCardClick={onCardClick}
                    onUpdateCard={onUpdateCard}
                    onRemoveCard={onRemoveCard}
                    onOpenPlanPopup={(card, plan) => {
                      const baseMV = card.effectiveMV ?? card.cmc;
                      const currentMVs = card.planCopyMVs?.[plan] || Array(card.quantity).fill(baseMV);
                      const currentSBs = card.planCopySideboard?.[plan] || Array(card.quantity).fill(false);
                      setPlanPopup({ card, plan, mvs: [...currentMVs], sbs: [...currentSBs] });
                    }}
                    planColors={planColors}
                    format={format}
                    advice={manaAdvice.advice}
                  />
                ) : (
                  /* List view - with column headers */
                  <div>
                    {/* Column headers - sticky when scrolling */}
                    <div className="sticky top-0 z-10 flex items-center px-2 py-1.5 text-[10px] text-white/40 uppercase tracking-wider border-b border-white/10 mb-1 bg-slate-900">
                      <span className="w-[68px] text-center">Qty</span>
                      <span className="flex-1 text-left">Name</span>
                      <span className="w-10 text-center">MV</span>
                      <span className="w-14 text-center" title="Total mana value (qty × MV)">Weight</span>
                      <span className="w-[90px] text-center" title="Card% / Land% / Combined%">On Curve</span>
                      <span className="w-[140px] text-center hidden sm:block">MATCH</span>
                      <span className="w-10 text-center hidden sm:block">SB</span>
                      <span className="w-8 text-center">X</span>
                    </div>
                    <div className="space-y-0.5">
                      {groupCards(displayDeck).map((group, groupIdx) => (
                        <div key={group.name}>
                          {/* Group separator */}
                          {groupBy !== 'none' && (
                            <div className="flex items-center gap-2 mt-2 mb-1 first:mt-0">
                              <span className="text-[10px] text-white/40 uppercase tracking-wider">{group.name}</span>
                              <div className="flex-1 h-px bg-white/10" />
                              <span className="text-[10px] text-white/30">{group.cards.reduce((s, c) => s + c.quantity, 0)}</span>
                            </div>
                          )}
                          {group.cards.map(card => {
                            const baseCard = deck.find(c => c.id === card.id);
                            const baseQty = baseCard?.quantity ?? card.quantity;
                            const baseMV = baseCard?.effectiveMV ?? baseCard?.cmc ?? card.cmc;

                            // Calculate plan-specific MV if viewing a plan
                            const planKey = activePlan as 'A' | 'B' | 'C' | 'D' | 'E';
                            const planCopyMVs = baseCard?.planCopyMVs?.[planKey];
                            const displayQty = card.quantity; // This is already filtered by getDisplayDeck

                            // Sum of MVs for this plan (or base * qty)
                            let planMVTotal = baseMV * displayQty;
                            let hasPlanOverride = false;
                            if (activePlan !== 'base' && planCopyMVs) {
                              // Sum only the copies that are in the main deck for this plan
                              const copySideboard = baseCard?.planCopySideboard?.[planKey] || [];
                              let mainDeckMVSum = 0;
                              planCopyMVs.forEach((mv, i) => {
                                if (!copySideboard[i]) { // Not sideboarded
                                  mainDeckMVSum += mv;
                                }
                              });
                              planMVTotal = mainDeckMVSum;
                              hasPlanOverride = planMVTotal !== baseMV * displayQty;
                            }

                            return (
                              <div
                                key={card.id}
                                className="flex items-center px-2 py-1 bg-white/5 hover:bg-white/10 rounded transition-colors group/card"
                                onMouseEnter={(e) => {
                                  setHoveredCard(card);
                                  setMousePos({ x: e.clientX, y: e.clientY });
                                }}
                                onMouseMove={(e) => setMousePos({ x: e.clientX, y: e.clientY })}
                                onMouseLeave={() => setHoveredCard(null)}
                              >
                                {/* Quantity controls: - # + x4 */}
                                <div className="flex items-center justify-center gap-0.5 w-[68px]">
                                  <button
                                    onClick={() => {
                                      // Always modify the base quantity
                                      const newQty = Math.max(0, baseQty - 1);
                                      if (newQty === 0) {
                                        onRemoveCard?.(card.id);
                                      } else {
                                        onUpdateCard?.(card.id, { quantity: newQty });
                                      }
                                    }}
                                    className="w-4 h-4 text-[10px] text-white/40 hover:text-white hover:bg-white/10 rounded flex items-center justify-center"
                                  >
                                    −
                                  </button>
                                  {/* Show match-specific quantity, with base in different color if different */}
                                  <span
                                    className={`w-4 text-center text-xs font-mono ${displayQty !== baseQty ? 'text-cyan-400' : 'text-white/60'}`}
                                    title={displayQty !== baseQty ? `Match quantity: ${displayQty} (base: ${baseQty})` : `Quantity: ${displayQty}`}
                                  >
                                    {displayQty}
                                  </span>
                                  <button
                                    onClick={() => onUpdateCard?.(card.id, { quantity: baseQty + 1 })}
                                    className="w-4 h-4 text-[10px] text-white/40 hover:text-white hover:bg-white/10 rounded flex items-center justify-center"
                                  >
                                    +
                                  </button>
                                  {/* Fill playset - hidden in singleton (commander) mode */}
                                  {!commanderIdentity && (
                                    <button
                                      onClick={() => onUpdateCard?.(card.id, { quantity: 4 })}
                                      className="px-1 h-4 text-[8px] text-cyan-400/60 hover:text-cyan-400 hover:bg-cyan-500/20 rounded flex items-center justify-center"
                                      title="Fill playset"
                                    >
                                      ×4
                                    </button>
                                  )}
                                </div>

                                {/* Flip button for DFCs */}
                                {card.backImageUrl && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      // Toggle isFlipped on the card (persistent)
                                      onUpdateCard?.(card.id, { isFlipped: !card.isFlipped });
                                      // Also update visual state for streaming
                                      setShowBackFace(!card.isFlipped);
                                    }}
                                    className={`p-0.5 rounded transition-colors ${card.isFlipped
                                      ? 'bg-purple-500 text-white'
                                      : 'text-purple-400/60 hover:text-purple-400 hover:bg-purple-500/20'
                                      }`}
                                    title={card.isFlipped ? "Show front face" : "Flip to back face"}
                                  >
                                    <RefreshCw className="size-3.5" />
                                  </button>
                                )}

                                {/* Card name - clickable, with rarity indicator */}
                                <span
                                  onClick={() => onCardClick?.(card)}
                                  className={`flex-1 text-sm cursor-pointer hover:text-cyan-300 truncate flex items-center gap-1 ${!isCardLegalForCommander(card) ? 'text-red-300' : 'text-white'}`}
                                  title={!isCardLegalForCommander(card) ? `${card.name} - Not legal for commander's color identity` : card.name}
                                >
                                  {!isCardLegalForCommander(card) && (
                                    <AlertTriangle className="size-3.5 text-red-400 flex-shrink-0" />
                                  )}
                                  {/* Show only front name (split on '//' for DFCs), or back name when flipped */}
                                  {card.isFlipped && card.backName
                                    ? card.backName
                                    : (card.name.includes(' // ') ? card.name.split(' // ')[0] : card.name)}
                                  {card.rarity && (
                                    <span className={`text-[9px] ml-1 ${card.rarity === 'M' ? 'text-orange-400' :
                                      card.rarity === 'R' ? 'text-amber-400' :
                                        card.rarity === 'U' ? 'text-gray-400' :
                                          'text-white/30'
                                      }`}>({card.rarity})</span>
                                  )}
                                  {card.entersTapped && (
                                    <span className="text-[8px] text-amber-400/60 ml-1">
                                      ETB{card.tapCondition === 'conditional' ? '⌛' : '⏳'}
                                    </span>
                                  )}
                                </span>

                                {/* Cost (MV) - Base: printed value per copy, Match: avg MV per copy */}
                                {(() => {
                                  // Base: printed MV per copy
                                  // Match: average MV per copy (weight ÷ qty)
                                  let displayMV = baseMV;
                                  let mvLabel = `${baseMV}`;

                                  if (activePlan !== 'base' && displayQty > 0) {
                                    // Calculate average MV per copy for this match
                                    const avgMV = planMVTotal / displayQty;
                                    displayMV = avgMV;

                                    // Show as integer if whole number, otherwise 1 decimal
                                    mvLabel = Number.isInteger(avgMV) ? `${avgMV}` : avgMV.toFixed(1);
                                  }

                                  const isOverride = activePlan !== 'base' && displayMV !== baseMV;

                                  return (
                                    <span
                                      className={`w-10 text-center text-sm font-mono ${card.isLand ? 'text-white/30' : isOverride
                                        ? 'text-purple-400 font-medium'
                                        : 'text-white/50'
                                        }`}
                                      title={card.isLand ? 'Land' : (isOverride
                                        ? `Match avg MV: ${mvLabel} (base: ${baseMV})`
                                        : `Base MV: ${baseMV}`
                                      )}
                                    >
                                      {card.isLand ? '' : mvLabel}
                                      {!card.isLand && isOverride && <span className="text-[8px] ml-0.5">⚡</span>}
                                    </span>
                                  );
                                })()}

                                {/* Weight - total mana contribution for this card (qty × MV) */}
                                {(() => {
                                  const weight = card.isLand ? 0 : (
                                    activePlan !== 'base' && displayQty > 0
                                      ? planMVTotal
                                      : baseMV * displayQty
                                  );
                                  const baseWeight = baseMV * (baseCard?.quantity ?? card.quantity);
                                  const isOverride = weight !== baseWeight && activePlan !== 'base';

                                  return (
                                    <span
                                      className={`w-14 text-center text-sm font-mono ${card.isLand ? 'text-white/30' : isOverride ? 'text-cyan-400 font-medium' : 'text-white/40'}`}
                                      title={card.isLand ? 'Land' : (isOverride
                                        ? `Match weight: ${weight} (base: ${baseWeight})`
                                        : `Weight: ${weight}`
                                      )}
                                    >
                                      {card.isLand ? '—' : weight}
                                    </span>
                                  );
                                })()}

                                {/* Probability - Card / Land / Combined - TURN for card draw, MV for lands */}
                                {(() => {
                                  if (card.isLand) {
                                    return <span className="w-[90px] text-center text-[10px] font-mono text-white/30">—</span>;
                                  }
                                  const displayMV = card.effectiveMV ?? card.cmc;
                                  const landCount = deck.filter(c => c.isLand).reduce((sum, c) => sum + c.quantity, 0);
                                  const playability = combinedPlayabilitySeparateTurns(totalCards, displayQty > 0 ? displayQty : card.quantity, landCount, probTargetTurn, displayMV, onThePlay);

                                  return (
                                    <span
                                      className="w-[90px] text-center text-[10px] font-mono"
                                      title={`See by T${probTargetTurn}: ${Math.round(playability.cardProb * 100)}% | ${displayMV}L by T${Math.max(probTargetTurn, displayMV)}: ${Math.round(playability.landProb * 100)}% | Combined: ${Math.round(playability.combined * 100)}% (${onThePlay ? 'Play' : 'Draw'})`}
                                    >
                                      <span className={getProbabilityColorClass(playability.cardProb)}>{Math.round(playability.cardProb * 100)}</span>
                                      <span className="text-white/30">/</span>
                                      <span className={getProbabilityColorClass(playability.landProb)}>{Math.round(playability.landProb * 100)}</span>
                                      <span className="text-white/30">/</span>
                                      <span className={getProbabilityColorClass(playability.combined)}>{Math.round(playability.combined * 100)}%</span>
                                    </span>
                                  );
                                })()}

                                {/* Match section: 1 2 3 4 5 buttons (formerly Plans A-E) - hidden on mobile */}
                                <div className="hidden sm:flex items-center gap-1 w-[140px] justify-center">
                                  {(['A', 'B', 'C', 'D', 'E'] as const).map((plan, idx) => {
                                    const matchNum = idx + 1; // A=1, B=2, etc.
                                    const planCopyMVs = baseCard?.planCopyMVs?.[plan];
                                    const planCopySB = baseCard?.planCopySideboard?.[plan];
                                    // Highlight if any copy has MV override OR sideboard override
                                    const hasOverride = (planCopyMVs && planCopyMVs.some((mv) => mv !== baseMV)) ||
                                      (planCopySB && planCopySB.some((sb) => sb === true));
                                    const colors = planColors[plan];

                                    return (
                                      <button
                                        key={plan}
                                        onClick={() => {
                                          // Open popup for per-copy cost assignment
                                          const currentMVs = planCopyMVs || Array(baseQty).fill(baseMV);
                                          const currentSBs = card.planCopySideboard?.[plan] || Array(baseQty).fill(false);
                                          setPlanPopup({
                                            card: baseCard || card,
                                            plan,
                                            mvs: [...currentMVs],
                                            sbs: [...currentSBs]
                                          });
                                        }}
                                        className="w-6 h-6 rounded text-[11px] font-medium transition-all"
                                        style={{
                                          backgroundColor: hasOverride ? colors.bg : `${colors.hex}15`,
                                          color: hasOverride ? colors.text : `${colors.hex}80`,
                                          boxShadow: hasOverride ? `inset 0 0 0 1px ${colors.border}` : 'none'
                                        }}
                                        title={hasOverride
                                          ? `Match ${matchNum}: ${planCopyMVs?.join(', ')} — Click to edit`
                                          : `Match ${matchNum}: Click to set costs`
                                        }
                                      >
                                        {matchNum}
                                      </button>
                                    );
                                  })}
                                </div>

                                {/* Move to sideboard button */}
                                <button
                                  onClick={() => onMoveToSideboard?.(card.id)}
                                  className="px-1.5 py-0.5 bg-amber-500/20 text-amber-400 hover:bg-amber-500/40 text-[10px] rounded hidden sm:block"
                                  title="Move to sideboard"
                                >
                                  →SB
                                </button>

                                {/* Remove */}
                                <button
                                  onClick={() => onRemoveCard?.(card.id)}
                                  className="w-8 text-center text-red-400/60 hover:text-red-400 text-[11px]"
                                  title="Remove card from deck"
                                >
                                  ×
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Sideboard - List/Grid views only (Dynamic view has its own Sideboard section) */}
            {viewMode !== 'dynamic' && displaySideboard.filter(c => !c.isCommander && !c.isCompanion && !c.isExtraCard).length > 0 && onCardClick && (
              <div className="border-t border-white/10 pt-3 mt-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white/80 text-sm">
                    Sideboard {activePlan !== 'base' && <span className="text-amber-400">(Match {activePlan === 'A' ? '1' : activePlan === 'B' ? '2' : activePlan === 'C' ? '3' : activePlan === 'D' ? '4' : '5'})</span>}
                  </span>
                  <span className="text-white/50 text-xs">
                    {displaySideboard.filter(c => !c.isCommander && !c.isCompanion && !c.isExtraCard).reduce((sum, c) => sum + c.quantity, 0)} cards
                    {activePlan !== 'base' && sideboard.filter(c => !c.isCommander && !c.isCompanion && !c.isExtraCard).reduce((sum, c) => sum + c.quantity, 0) !== displaySideboard.filter(c => !c.isCommander && !c.isCompanion && !c.isExtraCard).reduce((sum, c) => sum + c.quantity, 0) && (
                      <span className="text-amber-400 ml-1">(base: {sideboard.filter(c => !c.isCommander && !c.isCompanion && !c.isExtraCard).reduce((sum, c) => sum + c.quantity, 0)})</span>
                    )}
                  </span>
                </div>

                {viewMode === 'grid' && onUpdateSideboardCard && onRemoveFromSideboard ? (
                  <DeckGrid
                    groups={[{ name: 'Sideboard', cards: displaySideboard.filter(c => !c.isCommander && !c.isCompanion && !c.isExtraCard) }]}
                    onCardClick={onCardClick!}
                    onUpdateCard={onUpdateSideboardCard}
                    onRemoveCard={onRemoveFromSideboard}
                    onOpenPlanPopup={(card, plan) => {
                      const baseMV = card.effectiveMV ?? card.cmc;
                      // Find the original card to get the base quantity
                      const originalCard = [...deck, ...sideboard].find(c => c.id === card.id) || card;
                      const currentMVs = originalCard.planCopyMVs?.[plan] || Array(originalCard.quantity).fill(baseMV);
                      const currentSBs = originalCard.planCopySideboard?.[plan] || Array(originalCard.quantity).fill(false);
                      setPlanPopup({ card: originalCard, plan, mvs: [...currentMVs], sbs: [...currentSBs] });
                    }}
                    isSideboard={true}
                    planColors={planColors}
                  />
                ) : viewMode === 'grid' ? (
                  <DeckGrid
                    groups={[{ name: 'Sideboard', cards: displaySideboard.filter(c => !c.isCommander && !c.isCompanion && !c.isExtraCard) }]}
                    onCardClick={onCardClick!}
                    onUpdateCard={() => { }}
                    onRemoveCard={() => { }}
                    isSideboard={true}
                    planColors={planColors}
                  />
                ) : (
                  <div>
                    {/* Column headers - matching main deck structure exactly */}
                    <div className="sticky top-0 z-10 flex items-center px-2 py-1.5 text-[10px] text-white/40 uppercase tracking-wider border-b border-amber-500/20 mb-1 bg-slate-900">
                      <span className="w-[68px] text-center">Qty</span>
                      <span className="flex-1 text-left">Name</span>
                      <span className="w-10 text-center">MV</span>
                      <span className="w-12 text-center" title="Total mana value (qty × MV)">Weight</span>
                      <span className="w-14 text-center">On Curve</span>
                      <span className="w-[140px] text-center hidden sm:block">MATCH</span>
                      <span className="w-10 text-center hidden sm:block">MB</span>
                      <span className="w-8 text-center">X</span>
                    </div>
                    <div className="space-y-0.5">
                      {displaySideboard.filter(c => !c.isCommander && !c.isCompanion && !c.isExtraCard).map(card => {
                        // Find original card for base values
                        const originalCard = [...deck, ...sideboard].find(c => c.id === card.id) || card;
                        const baseQty = originalCard.quantity;
                        const displayQty = card.quantity;
                        const baseMV = originalCard.effectiveMV ?? originalCard.cmc;
                        const planKey = activePlan as 'A' | 'B' | 'C' | 'D' | 'E';

                        return (
                          <div
                            key={card.id}
                            className="flex items-center px-2 py-1 bg-amber-500/10 hover:bg-amber-500/20 rounded transition-colors group/card"
                            onMouseEnter={(e) => {
                              setHoveredCard(card);
                              setMousePos({ x: e.clientX, y: e.clientY });
                            }}
                            onMouseMove={(e) => setMousePos({ x: e.clientX, y: e.clientY })}
                            onMouseLeave={() => setHoveredCard(null)}
                          >
                            {/* Quantity controls: - # + x4 - matching main deck structure */}
                            <div className="flex items-center justify-center gap-0.5 w-[68px]">
                              <button
                                onClick={() => {
                                  if (activePlan === 'base') {
                                    onUpdateSideboardCard?.(card.id, { quantity: Math.max(1, card.quantity - 1) });
                                  } else {
                                    const planQty = card.planQuantities?.[planKey] ?? 0;
                                    if (planQty > 0) {
                                      onUpdatePlanQuantity?.(card.id, planKey, Math.max(0, planQty - 1));
                                    }
                                  }
                                }}
                                className="w-4 h-4 text-[10px] text-amber-300/40 hover:text-red-400 hover:bg-red-500/10 rounded flex items-center justify-center"
                                title="Decrease quantity"
                              >
                                −
                              </button>
                              <span
                                className={`w-4 text-center text-xs font-mono ${displayQty !== baseQty ? 'text-cyan-400' : 'text-amber-300/60'}`}
                                title={displayQty !== baseQty ? `Match quantity: ${displayQty} (base: ${baseQty})` : `Quantity: ${displayQty}`}
                              >
                                {displayQty}
                              </span>
                              <button
                                onClick={() => {
                                  if (activePlan === 'base') {
                                    onUpdateSideboardCard?.(card.id, { quantity: card.quantity + 1 });
                                  } else {
                                    const currentPlanQty = card.planQuantities?.[planKey] ?? 0;
                                    const baseQ = sideboard.find(c => c.id === card.id)?.quantity ?? card.quantity;
                                    onUpdatePlanQuantity?.(card.id, planKey, Math.min(baseQ, currentPlanQty + 1));
                                  }
                                }}
                                className="w-4 h-4 text-[10px] text-amber-300/40 hover:text-cyan-400 hover:bg-cyan-500/10 rounded flex items-center justify-center"
                                title="Increase quantity"
                              >
                                +
                              </button>
                              {/* Fill playset - hidden in singleton (commander) mode */}
                              {!commanderIdentity && (
                                <button
                                  onClick={() => onUpdateSideboardCard?.(card.id, { quantity: 4 })}
                                  className="px-1 h-4 text-[8px] text-cyan-400/60 hover:text-cyan-400 hover:bg-cyan-500/20 rounded flex items-center justify-center"
                                  title="Fill playset (4)"
                                >
                                  ×4
                                </button>
                              )}
                            </div>

                            {/* Card name - with commander identity warning */}
                            <span
                              onClick={() => onCardClick?.(card)}
                              className={`text-sm flex-1 cursor-pointer hover:text-amber-50 truncate flex items-center gap-1 ${!isCardLegalForCommander(card) ? 'text-red-300' : 'text-amber-100'}`}
                              title={!isCardLegalForCommander(card) ? `${card.name} - Not legal for commander's color identity` : card.name}
                            >
                              {!isCardLegalForCommander(card) && (
                                <AlertTriangle className="size-3 text-red-400 flex-shrink-0" />
                              )}
                              {card.name}
                            </span>

                            {/* MV - matches main deck structure */}
                            {(() => {
                              const mv = card.effectiveMV ?? card.cmc;
                              return (
                                <span
                                  className={`w-10 text-center text-sm font-mono ${card.isLand ? 'text-white/30' : 'text-amber-300/50'}`}
                                  title={card.isLand ? 'Land' : `MV: ${mv}`}
                                >
                                  {card.isLand ? '' : mv}
                                </span>
                              );
                            })()}

                            {/* Weight - matches main deck */}
                            {(() => {
                              const mv = card.effectiveMV ?? card.cmc;
                              const weight = card.isLand ? 0 : mv * displayQty;
                              return (
                                <span
                                  className={`w-12 text-center text-sm font-mono ${card.isLand ? 'text-white/30' : 'text-amber-300/40'}`}
                                  title={card.isLand ? 'Land' : `Weight: ${weight}`}
                                >
                                  {card.isLand ? '—' : weight}
                                </span>
                              );
                            })()}

                            {/* On Curve probability - TURN for card draw, MV for lands */}
                            {(() => {
                              if (card.isLand) {
                                return <span className="w-[90px] text-center text-[10px] font-mono text-amber-300/30">—</span>;
                              }
                              const displayMV = card.effectiveMV ?? card.cmc;
                              const landCount = deck.filter(c => c.isLand).reduce((sum, c) => sum + c.quantity, 0);
                              const playability = combinedPlayabilitySeparateTurns(totalCards, displayQty > 0 ? displayQty : card.quantity, landCount, probTargetTurn, displayMV, onThePlay);

                              return (
                                <span
                                  className="w-[90px] text-center text-[10px] font-mono"
                                  title={`See by T${probTargetTurn}: ${Math.round(playability.cardProb * 100)}% | ${displayMV}L by T${Math.max(probTargetTurn, displayMV)}: ${Math.round(playability.landProb * 100)}% | Combined: ${Math.round(playability.combined * 100)}% (${onThePlay ? 'Play' : 'Draw'})`}
                                >
                                  <span className={getProbabilityColorClass(playability.cardProb)}>{Math.round(playability.cardProb * 100)}</span>
                                  <span className="text-white/30">/</span>
                                  <span className={getProbabilityColorClass(playability.landProb)}>{Math.round(playability.landProb * 100)}</span>
                                  <span className="text-white/30">/</span>
                                  <span className={getProbabilityColorClass(playability.combined)}>{Math.round(playability.combined * 100)}%</span>
                                </span>
                              );
                            })()}

                            {/* Match buttons 1-5 - hidden on mobile */}
                            <div className="hidden sm:flex items-center gap-1 w-[140px] justify-center">
                              {(['A', 'B', 'C', 'D', 'E'] as const).map((plan, idx) => {
                                const matchNum = idx + 1;
                                // Find original card to get base quantity
                                const originalCard = [...deck, ...sideboard].find(c => c.id === card.id) || card;
                                const sbBaseMV = originalCard.effectiveMV ?? originalCard.cmc;
                                const planCopyMVs = originalCard.planCopyMVs?.[plan];
                                const planCopySB = originalCard.planCopySideboard?.[plan];
                                const hasOverride = (planCopyMVs && planCopyMVs.some(mv => mv !== sbBaseMV)) ||
                                  (planCopySB && planCopySB.some(sb => sb));
                                const colors = planColors[plan];

                                return (
                                  <button
                                    key={plan}
                                    onClick={() => {
                                      const currentMVs = planCopyMVs || Array(originalCard.quantity).fill(sbBaseMV);
                                      const currentSBs = planCopySB || Array(originalCard.quantity).fill(false);
                                      setPlanPopup({
                                        card: originalCard,
                                        plan,
                                        mvs: [...currentMVs],
                                        sbs: [...currentSBs]
                                      });
                                    }}
                                    className="w-6 h-6 rounded text-[11px] font-medium transition-all"
                                    style={{
                                      backgroundColor: hasOverride ? colors.bg : `${colors.hex}15`,
                                      color: hasOverride ? colors.text : `${colors.hex}80`,
                                      boxShadow: hasOverride ? `inset 0 0 0 1px ${colors.border}` : 'none'
                                    }}
                                    title={`Match ${matchNum}: Click to set MB/SB status`}
                                  >
                                    {matchNum}
                                  </button>
                                );
                              })}
                            </div>

                            {/* Move to main deck */}
                            <button
                              onClick={() => onMoveToMainDeck?.(card.id)}
                              className="px-1.5 py-0.5 bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/40 text-[10px] rounded hidden sm:block"
                              title="Move to main deck"
                            >
                              →MB
                            </button>

                            {/* Remove */}
                            <button
                              onClick={() => onRemoveFromSideboard?.(card.id)}
                              className="w-8 text-center text-red-400/60 hover:text-red-400 text-[11px]"
                              title="Remove card"
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Extra Cards - Planes, Schemes, Vanguard, Conspiracy - List/Grid views only */}
            {viewMode !== 'dynamic' && [...deck, ...sideboard].some(c => c.isExtraCard) && onCardClick && (
              <div className="mt-4 bg-white/5 rounded-lg border border-white/10 p-3">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-purple-400 text-sm font-medium flex items-center gap-2">
                    <span>Other</span>
                    <span className="text-xs px-2 py-0.5 rounded bg-purple-500/20 text-purple-400">
                      {[...deck, ...sideboard].filter(c => c.isExtraCard).reduce((sum, c) => sum + c.quantity, 0)} cards
                    </span>
                  </h4>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-1">
                  {[...deck, ...sideboard].filter(c => c.isExtraCard).map(card => (
                    <div
                      key={card.id}
                      className="h-[24px] px-2 flex items-center justify-center bg-purple-500/10 hover:bg-purple-500/20 rounded text-[9px] text-purple-400/90 cursor-pointer transition-colors"
                      title={`${card.name}${card.extraCardType ? ` (${card.extraCardType})` : ''}`}
                      onMouseEnter={(e) => {
                        setHoveredCard(card);
                        setMousePos({ x: e.clientX, y: e.clientY });
                      }}
                      onMouseMove={(e) => setMousePos({ x: e.clientX, y: e.clientY })}
                      onMouseLeave={() => setHoveredCard(null)}
                      onClick={() => onCardClick(card)}
                    >
                      {card.quantity > 1 && <span className="mr-1 text-purple-400/60">{card.quantity}×</span>}
                      <span className="truncate text-center">{card.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}


          </div>
        )}


        {/* Dynamic View - MVD Grid (BUILD tab only) */}
        {activeTab === 'build' && viewMode === 'dynamic' && (
          <div className="space-y-4">

            {/* MV Distribution Grid */}
            <div className="bg-white/5 rounded-lg p-4 border border-white/10">
              {/* MV Visual Grid - Adaptive to deck's max MV AND anchor distribution */}
              {(() => {
                // Calculate max MV in deck - considers base MV AND per-copy plan overrides
                const deckMaxMV = displayDeck.reduce((max, card) => {
                  if (isEffectivelyLand(card) || card.isExtraCard) return max;

                  // Start with base MV
                  const baseMV = card.effectiveMV ?? card.cmc;
                  let cardMax = baseMV;

                  // Check for per-copy MV overrides in active plan (these can be 7, 8, 9+)
                  if (activePlan !== 'base') {
                    const planKey = activePlan as 'A' | 'B' | 'C' | 'D' | 'E';
                    const copyMVs = card.planCopyMVs?.[planKey];
                    if (copyMVs && copyMVs.length > 0) {
                      cardMax = Math.max(cardMax, ...copyMVs);
                    }
                  }

                  return Math.max(max, cardMax);
                }, 0);

                // Use the larger of: deck's max MV, anchor distribution length, or 6
                // If maxMVCap is set, use that as the limit instead
                const maxMV = maxMVCap !== null
                  ? maxMVCap
                  : Math.max(deckMaxMV, anchorDistribution.length - 1, 6);

                // Generate MV slots from minMVFloor (or 0) to maxMV
                const startMV = minMVFloor !== null ? minMVFloor : 0;
                const mvSlots = Array.from({ length: maxMV - startMV + 1 }, (_, i) => i + startMV);

                return (
                  <div className="flex gap-1.5 overflow-x-auto snap-x snap-mandatory pb-1.5 -mx-1 px-1" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.2) transparent' }}>
                    {mvSlots.map((mv) => {
                      // Get card copies at this MV, respecting per-copy overrides for active plan
                      const cardCopies: { card: typeof displayDeck[0]; copyIndex: number; copyMV: number; isBackFace?: boolean; isMarkedAsLand?: boolean }[] = [];

                      displayDeck.forEach(card => {
                        if (isEffectivelyLand(card) || card.isExtraCard) return;

                        // For each copy of this card, determine its MV based on overrides
                        for (let i = 0; i < card.quantity; i++) {
                          const planKey = activePlan as 'A' | 'B' | 'C' | 'D' | 'E';
                          const backIsLand = card.backType?.toLowerCase().includes('land');

                          // Check if this copy is flagged to use back face
                          const isFlaggedBack = activePlan !== 'base' &&
                            card.isDoubleFaced &&
                            card.planCopyFace?.[planKey]?.[i] === 1;

                          // MDFC: Actually plays as land (remove from curve)
                          // Transform: Still cast as spell, but marked as "will become land"
                          const isModalDfcPlayingAsLand = isFlaggedBack && card.layout === 'modal_dfc' && backIsLand;
                          const isTransformMarkedAsLand = isFlaggedBack && card.layout === 'transform' && backIsLand;

                          // If MDFC using land back face, skip from curve (shows in lands section)
                          if (isModalDfcPlayingAsLand) {
                            continue; // This copy treated as a land in this plan
                          }

                          let copyMV: number;

                          // Check for per-copy MV override in active plan
                          if (activePlan !== 'base' && card.planCopyMVs?.[planKey]?.[i] !== undefined) {
                            copyMV = card.planCopyMVs![planKey]![i]!;
                          } else if (activePlan !== 'base' && card.planMVs?.[planKey] !== undefined) {
                            copyMV = card.planMVs[planKey];
                          } else if (isFlaggedBack && card.layout === 'modal_dfc' && card.backCmc !== undefined) {
                            // MDFC using back face that's not a land - use back face CMC
                            copyMV = card.backCmc;
                          } else {
                            // Use base effectiveMV or cmc (Transform cards always use front MV)
                            copyMV = card.effectiveMV ?? card.cmc;
                          }

                          // Check if this copy belongs in this MV slot
                          if (copyMV === mv) {
                            cardCopies.push({
                              card,
                              copyIndex: i,
                              copyMV,
                              isBackFace: isFlaggedBack && card.layout === 'modal_dfc',
                              isMarkedAsLand: isTransformMarkedAsLand // Transform card marked as will-become-land
                            });
                          }
                        }
                      });

                      // Get anchor count (extend distribution if needed)
                      const anchorCount = mv < anchorDistribution.length ? anchorDistribution[mv] : 0;
                      const diff = cardCopies.length - anchorCount;

                      // Skip columns that are 0/0 (no cards, no target, and not locked)
                      if (cardCopies.length === 0 && anchorCount === 0 && !lockedMVs.includes(mv)) return null;

                      // Check if this slot matches the current parity filter AND excludeZero filter
                      const isValidParity = (mvParity === 'all' ||
                        (mvParity === 'odds' && mv % 2 === 1) ||
                        (mvParity === 'evens' && mv % 2 === 0)) &&
                        !(excludeZero && mv === 0); // MV 0 is invalid when excludeZero is active

                      // Hide invalid/excluded columns entirely (unless they have cards - show warning)
                      if (!isValidParity && cardCopies.length === 0) return null;

                      return (
                        <DropCurveColumn
                          key={mv}
                          mv={mv}
                          cardCopies={cardCopies}
                          anchorCount={anchorCount}
                          isValidParity={isValidParity}
                          probTargetTurn={probTargetTurn}
                          rampBoost={deckManaProfile.rampForMV(mv)}
                          drawBoost={deckManaProfile.drawForMV(mv)}
                          effectiveTargetLands={effectiveTargetLands}
                          targetDeckSize={targetDeckSize}
                          targetManaCurve={targetManaCurve}
                          onThePlay={onThePlay}
                          maxMVCap={maxMVCap}
                          setMaxMVCap={setMaxMVCap}
                          minMVFloor={minMVFloor}
                          setMinMVFloor={setMinMVFloor}
                          displayDeck={displayDeck}
                          isEffectivelyLand={isEffectivelyLand}
                          anchorDistribution={anchorDistribution}
                          deck={deck}
                          sideboard={sideboard}
                          activePlan={activePlan}
                          onCardClick={onCardClick}
                          setHoveredCard={setHoveredCard}
                          setMousePos={setMousePos}
                          onRemoveCard={onRemoveCard}
                          onUpdateCard={onUpdateCard}
                          onUpdatePlanCopyMVs={onUpdatePlanCopyMVs}
                          onUpdateSideboardCard={onUpdateSideboardCard}
                          onAddToSideboard={onAddToSideboard}
                          onUpdatePlanQuantity={onUpdatePlanQuantity}
                          onAddCard={onAddCard}
                          setBuildSearchMV={setBuildSearchMV}
                          isLocked={lockedMVs.includes(mv)}
                          onToggleLock={() => toggleLockMV(mv)}
                          isSingletonFormat={isCommanderFormat(format) || Boolean(commanderIdentity)}
                        />
                      );
                    })}
              </div>
            );
          })()}
        </div>

            {/* Pip Counter Meter Widget & Smart Advice Box in Dynamic View (under non-land curve columns, above lands/charts) */}
              {deck.length > 0 && (
                <div className="space-y-3 my-4">
                  <div className="flex items-center justify-center gap-3 flex-wrap bg-slate-950/80 border border-purple-500/30 rounded-lg px-3 py-1.5 mx-auto w-fit text-xs font-medium">
                    <BrewTooltip
                      title="Colored Mana Pip Meter"
                      description="Counts every colored mana symbol across all nonland spells in your deck to determine how many sources of each color your manabase needs."
                      accent="purple"
                    >
                      <span className="text-purple-400 font-semibold flex items-center gap-1 cursor-help">
                        <span>Pip Meter:</span>
                      </span>
                    </BrewTooltip>
                    <div className="flex items-center gap-2 flex-wrap">
                      {(['W', 'U', 'B', 'R', 'G', 'C'] as const).map(c => {
                        const count = manaAdvice.pipStats?.pips?.[c] || 0;
                        if (count === 0 && c === 'C') return null;
                        const colorLabel = c === 'W' ? 'White' : c === 'U' ? 'Blue' : c === 'B' ? 'Black' : c === 'R' ? 'Red' : c === 'G' ? 'Green' : 'Colorless';
                        return (
                          <BrewTooltip
                            key={c}
                            title={`${colorLabel} Mana Pips`}
                            badge={`${count} pips`}
                            description={`Your spells require ${count} total ${colorLabel} mana symbol(s).`}
                            accent="slate"
                          >
                            <div className="flex items-center gap-1 bg-white/5 border border-white/10 px-1.5 py-0.5 rounded cursor-help">
                              <ManaSymbol symbol={c} size="sm" />
                              <span className="text-white font-mono font-medium text-xs">{count}</span>
                            </div>
                          </BrewTooltip>
                        );
                      })}
                    </div>

                    {handleAutoSuggestLands && (
                      <BrewTooltip
                        title="Auto-Balance Basic Lands"
                        badge={`${effectiveTargetLands} Target Lands`}
                        description="Automatically adds or rebalances basic lands in your deck proportional to your colored mana pips so your total lands match your Target Land count."
                        accent="cyan"
                      >
                        <button
                          type="button"
                          onClick={handleAutoSuggestLands}
                          className="px-2.5 py-1 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white text-xs font-medium rounded border border-cyan-400/50 shadow-md transition-all ml-1"
                        >
                          Suggest Lands
                        </button>
                      </BrewTooltip>
                    )}
                  </div>

                  {/* Smart Dynamic Mana Advice Box */}
                  <SmartAdviceBox advice={manaAdvice.advice} />
                </div>
              )}

              {/* Lands Section - Prominent Container - shows when there are lands OR MDFC land-back copies */}
              {displayLandCount > 0 && (
                <div className="mt-4 rounded-lg border border-amber-500/20 overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium bg-gradient-to-r from-amber-500/20 to-purple-500/10 hover:from-amber-500/30 hover:to-purple-500/20 transition-colors"
                    onClick={() => setLandsExpanded(!landsExpanded)}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-amber-400">Lands</span>
                      <span className={`text-xs px-2 py-0.5 rounded ${displayLandCount === effectiveTargetLands ? 'bg-green-500/20 text-green-400' :
                        displayLandCount > effectiveTargetLands ? 'bg-amber-500/20 text-amber-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                        {displayLandCount} / {effectiveTargetLands} target
                      </span>
                      {virtualLandCount > 0 && (
                        <BrewTooltip
                          title="Virtual Transform Lands"
                          badge={`+${virtualLandCount}`}
                          description="Transform cards marked as converting into mid-game land sources."
                          accent="purple"
                        >
                          <span className="text-xs px-2 py-0.5 rounded bg-purple-500/20 text-purple-400">
                            +{virtualLandCount} virtual
                          </span>
                        </BrewTooltip>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {onAddCard && landsExpanded && (
                        <span
                          className="text-[10px] px-2 py-1 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 rounded transition-colors"
                          onClick={(e) => { e.stopPropagation(); setBuildSearchMV(-1); }}
                        >
                          + Add Land
                        </span>
                      )}
                      <span className={`text-purple-400 text-xs transition-transform ${landsExpanded ? 'rotate-180' : ''}`}>▼</span>
                    </div>
                  </button>
                  {landsExpanded && (
                    <div className="p-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1">
                      {/* Regular lands AND flipped MDFCs with land backs */}
                      {displayDeck.filter(c => isEffectivelyLand(c)).map(card => {
                        // Is this a flipped MDFC (not a regular land)?
                        const isFlippedMDFC = !card.isLand && card.isFlipped;
                        const displayName = card.isFlipped && card.backName
                          ? card.backName
                          : (card.name.includes(' // ') ? card.name.split(' // ')[0] : card.name);

                        return (
                          <div
                            key={card.id}
                            className={`h-[26px] flex items-center rounded text-[9px] cursor-pointer transition-colors ${isFlippedMDFC
                              ? 'bg-purple-500/20 hover:bg-purple-500/30 text-purple-400'
                              : 'bg-amber-500/10 hover:bg-amber-500/20 text-amber-400/90'
                              }`}
                            onClick={() => onCardClick?.(card)}
                            onMouseEnter={(e) => {
                              setHoveredCard(card);
                              setMousePos({ x: e.clientX, y: e.clientY });
                            }}
                            onMouseMove={(e) => setMousePos({ x: e.clientX, y: e.clientY })}
                            onMouseLeave={() => setHoveredCard(null)}
                          >
                            {/* Minus button */}
                            <BrewTooltip
                              title="Remove 1 Copy"
                              description={`Removes 1 copy of ${displayName} from your lands.`}
                              accent="red"
                              className="h-full flex-shrink-0"
                            >
                              <button
                                type="button"
                                className={`w-5 h-full flex items-center justify-center text-[10px] text-red-400/50 hover:text-red-400 hover:bg-red-500/30 rounded-l border-r flex-shrink-0 ${isFlippedMDFC ? 'border-purple-500/20' : 'border-amber-500/20'}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const newQty = card.quantity - 1;
                                  if (newQty <= 0) {
                                    onRemoveCard?.(card.id);
                                  } else {
                                    onUpdateCard?.(card.id, { quantity: newQty });
                                  }
                                }}
                              >−</button>
                            </BrewTooltip>

                            {/* Flip button for flipped MDFCs */}
                            {card.backImageUrl && (
                              <BrewTooltip
                                title={card.isFlipped ? 'Flip to Spell Face' : 'Flip to Land Face'}
                                description={card.isFlipped ? 'Counts this modal double-faced card on your spell curve instead of lands.' : 'Counts this modal double-faced card as a land source.'}
                                accent="purple"
                                className="h-full flex-shrink-0"
                              >
                                <button
                                  type="button"
                                  className={`w-5 h-full flex items-center justify-center text-[8px] flex-shrink-0 ${card.isFlipped
                                    ? 'bg-purple-500 text-white'
                                    : 'text-purple-400/50 hover:text-purple-400 hover:bg-purple-500/30'
                                    }`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onUpdateCard?.(card.id, { isFlipped: !card.isFlipped });
                                    setShowBackFace(!card.isFlipped);
                                  }}
                                >
                                  <RefreshCw className="size-2.5" />
                                </button>
                              </BrewTooltip>
                            )}

                            {/* Card name and quantity */}
                            <span
                              className="flex-1 text-center truncate px-1 cursor-pointer"
                              onClick={() => onCardClick?.(card)}
                            >
                              {card.quantity > 1 && <span className={isFlippedMDFC ? 'text-purple-400/60' : 'text-amber-400/60'}>{card.quantity}× </span>}
                              {displayName}
                            </span>

                            {/* SB button — move land to sideboard */}
                            {!sideboard.some(c => c.isCommander) && (
                              <BrewTooltip
                                title={activePlan === 'base' ? 'Move to Sideboard' : 'Sideboard in Match Plan'}
                                badge="+SIDE"
                                description={activePlan === 'base' ? `Moves 1 copy of ${displayName} to your sideboard.` : `Moves 1 copy of ${displayName} to your sideboard for this matchup plan.`}
                                accent="amber"
                                className="h-full flex-shrink-0"
                              >
                                <button
                                  type="button"
                                  className={`w-5 h-full flex items-center justify-center text-[7px] flex-shrink-0 border-l ${isFlippedMDFC ? 'border-purple-500/20 text-purple-400/50 hover:text-purple-400 hover:bg-purple-500/30' : 'border-amber-500/20 text-amber-400/50 hover:text-amber-400 hover:bg-amber-500/30'}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (activePlan === 'base') {
                                      if (card.quantity > 0) {
                                        const sideboardCard = sideboard.find(c => c.name === card.name);
                                        if (sideboardCard) {
                                          onUpdateSideboardCard?.(sideboardCard.id, { quantity: sideboardCard.quantity + 1 });
                                        } else {
                                          onAddToSideboard?.({ ...card, id: `${card.id}-side-${Date.now()}`, quantity: 1, isSideboard: true });
                                        }
                                        if (card.quantity <= 1) {
                                          onRemoveCard?.(card.id);
                                        } else {
                                          onUpdateCard?.(card.id, { quantity: card.quantity - 1 });
                                        }
                                      }
                                    } else {
                                      const planKey = activePlan as 'A' | 'B' | 'C' | 'D' | 'E';
                                      const currentMain = card.planQuantities?.[planKey] ?? card.quantity;
                                      const newMain = Math.max(currentMain - 1, 0);
                                      onUpdatePlanQuantity?.(card.id, planKey, newMain);
                                    }
                                  }}
                                >SB</button>
                              </BrewTooltip>
                            )}

                            {/* Plus button */}
                            <BrewTooltip
                              title="Add 1 Copy"
                              description={`Adds 1 copy of ${displayName} to your lands.`}
                              accent="emerald"
                              className="h-full flex-shrink-0"
                            >
                              <button
                                type="button"
                                className={`w-5 h-full flex items-center justify-center text-[10px] text-green-400/50 hover:text-green-400 hover:bg-green-500/30 rounded-r border-l flex-shrink-0 ${isFlippedMDFC ? 'border-purple-500/20' : 'border-amber-500/20'}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onUpdateCard?.(card.id, { quantity: card.quantity + 1 });
                                }}
                              >+</button>
                            </BrewTooltip>
                          </div>
                        );
                      })}

                      {/* MDFC copies using land back faces (match plans only, modal_dfc only) */}
                      {activePlan !== 'base' && (() => {
                        const planKey = activePlan as 'A' | 'B' | 'C' | 'D' | 'E';
                        const mdfcLandCopies: { card: typeof displayDeck[0]; copyIndex: number }[] = [];

                        displayDeck.forEach(card => {
                          // Only modal_dfc - Transform cards must be cast front-first
                          if (card.layout === 'modal_dfc' && card.backType?.toLowerCase().includes('land') && !card.isLand) {
                            const copyFaces = card.planCopyFace?.[planKey] || [];
                            for (let i = 0; i < card.quantity; i++) {
                              if (copyFaces[i] === 1) {
                                mdfcLandCopies.push({ card, copyIndex: i });
                              }
                            }
                          }
                        });

                        return mdfcLandCopies.map(({ card, copyIndex }) => (
                          <div
                            key={`${card.id}-land-${copyIndex}`}
                            className="h-[26px] flex items-center bg-purple-500/10 hover:bg-purple-500/20 rounded text-[9px] text-purple-400/90 transition-colors border border-purple-500/20"
                            onMouseEnter={(e) => {
                              setHoveredCard(card);
                              setMousePos({ x: e.clientX, y: e.clientY });
                            }}
                            onMouseMove={(e) => setMousePos({ x: e.clientX, y: e.clientY })}
                            onMouseLeave={() => setHoveredCard(null)}
                          >
                            {/* Flip button - switch back to front face */}
                            {onUpdatePlanCopyFace && (
                              <BrewTooltip
                                title="Switch to Spell Face"
                                description={`Returns this copy to ${card.name.split(' // ')[0]} on your spell curve for this matchup.`}
                                accent="purple"
                                className="h-full flex-shrink-0"
                              >
                                <button
                                  type="button"
                                  className="w-5 h-full flex items-center justify-center text-[9px] text-purple-400 hover:bg-purple-500/30 rounded-l border-r border-purple-500/20 flex-shrink-0"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const currentFaces = card.planCopyFace?.[planKey] || Array(card.quantity).fill(0);
                                    const newFaces = [...currentFaces];
                                    newFaces[copyIndex] = 0; // Back to front face
                                    onUpdatePlanCopyFace(card.id, planKey, newFaces);
                                  }}
                                >
                                  <RefreshCw className="size-2.5" />
                                </button>
                              </BrewTooltip>
                            )}

                            {/* Card name (back face name) */}
                            <span
                              className="flex-1 text-center truncate px-1 cursor-pointer text-[8px]"
                              onClick={() => onCardClick?.(card)}
                            >
                              {card.backName}
                            </span>
                          </div>
                        ));
                      })()}

                      {displayLandCount < effectiveTargetLands && onAddCard && (
                        <button
                          className="h-[24px] px-2 border border-dashed border-amber-500/30 hover:border-amber-500/50 rounded flex items-center justify-center text-[9px] text-amber-400/40 hover:text-amber-400/70 transition-colors"
                          onClick={() => setBuildSearchMV(-1)}
                        >
                          + ({effectiveTargetLands - displayLandCount} more)
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

            {/* Mana Curve Section - Always visible, collapsible body */}
            {
              (() => {
                const analysisCards = deck.filter(c => !c.isExtraCard);
                return (
                  <div className="mt-4 rounded-lg border border-purple-500/20 overflow-hidden">
                    <button
                      className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium bg-gradient-to-r from-purple-500/20 to-amber-500/10 hover:from-purple-500/30 hover:to-amber-500/20 transition-colors"
                      onClick={() => setCurveExpanded(!curveExpanded)}
                    >
                      <span className="text-amber-400">Mana Curve</span>
                      <span className={`text-purple-400 text-xs transition-transform ${curveExpanded ? 'rotate-180' : ''}`}>▼</span>
                    </button>
                    {curveExpanded && (
                      <div className="p-3 h-[320px]">
                        <ManaCurveChart
                          deck={analysisCards}
                          analysis={analysis}
                          inputMode="import"
                          activeAnchor={activePlan === 'base' ? 'Ideal' : activePlan as 'A' | 'B' | 'C' | 'D' | 'E'}
                          onAnchorChange={(anchor) => setActivePlan(anchor === 'Ideal' ? 'base' : anchor)}
                          targetDeckSize={targetDeckSize}
                          targetLands={effectiveTargetLands}
                          targetRatio={effectiveTargetRatio}
                          mvParity={mvParity}
                          excludeZero={excludeZero}
                          anchorDistribution={anchorDistribution}
                          landCount={effectiveTargetLands}
                          totalCards={targetDeckSize}
                          onThePlay={onThePlay}
                        />
                      </div>
                    )}
                  </div>
                );
              })()
            }

            {/* Probability Calculators Section - Always visible, collapsible body */}
            {
              (() => {
                const analysisCards = deck.filter(c => !c.isExtraCard);
                const analysisLandCount = analysisCards.filter(c => c.isLand).reduce((sum, c) => sum + c.quantity, 0);
                const analysisTotalCards = analysisCards.reduce((sum, c) => sum + c.quantity, 0);
                return (
                  <div className="mt-4 rounded-lg border border-purple-500/20 overflow-hidden">
                    <button
                      className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium bg-gradient-to-r from-purple-500/20 to-amber-500/10 hover:from-purple-500/30 hover:to-amber-500/20 transition-colors"
                      onClick={() => setCalcExpanded(!calcExpanded)}
                    >
                      <span className="text-amber-400">Probability & On-Curve Land Calculators</span>
                      <span className={`text-purple-400 text-xs transition-transform ${calcExpanded ? 'rotate-180' : ''}`}>▼</span>
                    </button>
                    {calcExpanded && (
                      <div className="p-2.5 space-y-3">
                        {/* Dedicated Turn-by-Turn Land & On-Curve Probability Calculator */}
                        <LandProbabilityTable
                          landCount={displayLandCount}
                          totalCards={displayDeckCards}
                          targetDeckSize={targetDeckSize}
                          targetLands={effectiveTargetLands}
                          onThePlay={onThePlay}
                          onTogglePlayDraw={setOnThePlay}
                          onApplyTargetLands={(lands) => setBuildTargetLands(lands)}
                        />

                        {/* Collapsible Specific Card Hand Odds & Color Distribution */}
                        <div className="rounded-lg border border-white/10 bg-slate-950/40 overflow-hidden">
                          <button
                            type="button"
                            onClick={() => setHandCalcExpanded(!handCalcExpanded)}
                            className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold bg-white/[0.04] hover:bg-white/[0.08] transition-colors"
                          >
                            <span className="text-purple-200">Specific Card Hand Odds & Color Distribution</span>
                            <span className={`text-purple-400 text-xs transition-transform ${handCalcExpanded ? 'rotate-180' : ''}`}>▼</span>
                          </button>
                          {handCalcExpanded && (
                            <div className="p-2.5 flex flex-col sm:flex-row gap-2 border-t border-white/10">
                        {/* Hand Probability Calculator */}
                        <div className="flex-1 flex flex-col gap-2">
                          {/* Shared Controls Row: Play/Draw + Match */}
                          <div className="flex items-center gap-2">
                            <BrewTooltip
                              title={onThePlay ? 'On the Play' : 'On the Draw'}
                              badge={onThePlay ? 'No T1 Draw' : '+1 T1 Draw'}
                              description="Toggles whether you draw a card on Turn 1 when computing specific card & land odds."
                              accent={onThePlay ? 'purple' : 'amber'}
                            >
                              <button
                                type="button"
                                className={`px-2 py-1 rounded text-xs font-medium transition-colors whitespace-nowrap ${onThePlay ? 'bg-purple-500/30 text-purple-400 border border-purple-500/30' : 'bg-amber-500/30 text-amber-400 border border-amber-500/30'}`}
                                onClick={() => setOnThePlay(!onThePlay)}
                              >
                                {onThePlay ? 'Play' : 'Draw'}
                              </button>
                            </BrewTooltip>
                            <BrewTooltip
                              title="Deck Configuration"
                              badge={cardProbPlan === 'base' ? 'Base' : cardProbPlan}
                              description="Evaluates card copy counts using your Base mainboard or a post-sideboard Matchup Plan (M1–M5)."
                              accent="cyan"
                              className="flex-1"
                            >
                              <select
                                className="w-full px-2 py-1 bg-slate-900 border border-white/10 rounded text-white text-xs"
                                value={cardProbPlan}
                                onChange={(e) => setCardProbPlan(e.target.value as 'base' | 'A' | 'B' | 'C' | 'D' | 'E')}
                              >
                                <option value="base" className="bg-slate-900 text-white">Base</option>
                                <option value="A" className="bg-slate-900 text-white">M1</option>
                                <option value="B" className="bg-slate-900 text-white">M2</option>
                                <option value="C" className="bg-slate-900 text-white">M3</option>
                                <option value="D" className="bg-slate-900 text-white">M4</option>
                                <option value="E" className="bg-slate-900 text-white">M5</option>
                              </select>
                            </BrewTooltip>
                          </div>

                          {/* Specific Card Probability Section */}
                          <div className="bg-white/5 rounded p-2 border border-white/10 space-y-1.5">
                            <div className="flex items-center justify-between">
                              <span className="text-white/60 text-xs font-medium">Specific Card</span>
                              <BrewTooltip
                                title="Add Spell Slot"
                                description="Adds another spell requirement to estimate seeing multiple combo or synergy pieces together."
                                accent="amber"
                              >
                                <button
                                  type="button"
                                  className="text-xs px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 disabled:opacity-30"
                                  disabled={(1 + extraCardSlots.length + 1 + extraLandSlots.length) >= 8}
                                  onClick={() => setExtraCardSlots([...extraCardSlots, { id: null, minCopies: 1 }])}
                                >+</button>
                              </BrewTooltip>
                            </div>
                            <div className="flex items-center gap-1">
                              <BrewTooltip
                                title="Target Spell"
                                description="Choose a nonland card from your deck to compute the probability of drawing it by your selected turn."
                                accent="purple"
                                className="flex-1 min-w-0"
                              >
                                <select
                                  className="w-full px-2 py-1 bg-slate-900 border border-white/10 rounded text-white text-xs"
                                  value={selectedCardForProb || ''}
                                  onChange={(e) => setSelectedCardForProb(e.target.value || null)}
                                >
                                  <option value="" className="bg-slate-900 text-white">Select spell...</option>
                                  {analysisCards.filter(c => !c.isLand).map(card => (
                                    <option key={card.id} value={card.id} className="bg-slate-900 text-white">
                                      {card.quantity}x {card.name.length > 20 ? card.name.slice(0, 20) + '...' : card.name}
                                    </option>
                                  ))}
                                </select>
                              </BrewTooltip>
                              <BrewTooltip
                                title="Minimum Copies Needed"
                                badge={`≥${cardProbMinCopies}`}
                                description="Minimum number of copies of this spell you want to have drawn."
                                accent="purple"
                              >
                                <NumericStepper
                                  value={cardProbMinCopies}
                                  min={1}
                                  max={Math.max(4, selectedCardForProb ? (analysisCards.find(c => c.id === selectedCardForProb)?.quantity || 4) : 4)}
                                  onChange={setCardProbMinCopies}
                                  size="xs"
                                  accent="purple"
                                  inputWidthClass="w-6"
                                  ariaLabel="Minimum spell copies needed"
                                />
                              </BrewTooltip>
                            </div>
                            {extraCardSlots.map((slot, idx) => (
                              <div key={idx} className="flex items-center gap-1">
                                <select
                                  className="flex-1 min-w-0 px-2 py-1 bg-slate-900 border border-white/10 rounded text-white text-xs"
                                  value={slot.id || ''}
                                  onChange={(e) => {
                                    const next = [...extraCardSlots];
                                    next[idx] = { ...slot, id: e.target.value || null };
                                    setExtraCardSlots(next);
                                  }}
                                >
                                  <option value="" className="bg-slate-900 text-white">Select spell #{idx + 2}...</option>
                                  {analysisCards.filter(c => !c.isLand).map(card => (
                                    <option key={card.id} value={card.id} className="bg-slate-900 text-white">
                                      {card.quantity}x {card.name.length > 18 ? card.name.slice(0, 18) + '...' : card.name}
                                    </option>
                                  ))}
                                </select>
                                <NumericStepper
                                  value={slot.minCopies}
                                  min={1}
                                  max={4}
                                  onChange={(val) => {
                                    const next = [...extraCardSlots];
                                    next[idx] = { ...slot, minCopies: val };
                                    setExtraCardSlots(next);
                                  }}
                                  size="xs"
                                  accent="purple"
                                  inputWidthClass="w-6"
                                  ariaLabel={`Minimum copies for spell slot ${idx + 2}`}
                                />
                                <BrewTooltip title="Remove Slot" description="Removes this additional spell requirement." accent="red">
                                  <button
                                    type="button"
                                    onClick={() => setExtraCardSlots(extraCardSlots.filter((_, i) => i !== idx))}
                                    className="px-1.5 py-0.5 text-xs text-red-400/70 hover:text-red-300 bg-red-500/10 hover:bg-red-500/20 rounded"
                                  >×</button>
                                </BrewTooltip>
                              </div>
                            ))}
                          </div>

                          {/* Specific Land Probability Section */}
                          <div className="bg-white/5 rounded p-2 border border-white/10 space-y-1.5">
                            <div className="flex items-center justify-between">
                              <span className="text-white/60 text-xs font-medium">Specific Land</span>
                              <BrewTooltip
                                title="Add Land Slot"
                                description="Adds another specific land requirement to evaluate multi-land draws."
                                accent="amber"
                              >
                                <button
                                  type="button"
                                  className="text-xs px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 disabled:opacity-30"
                                  disabled={(1 + extraCardSlots.length + 1 + extraLandSlots.length) >= 8}
                                  onClick={() => setExtraLandSlots([...extraLandSlots, { id: null, minCopies: 1 }])}
                                >+</button>
                              </BrewTooltip>
                            </div>
                            <div className="flex items-center gap-1">
                              <BrewTooltip
                                title="Target Land"
                                description="Choose a specific land card in your deck to check your odds of seeing it."
                                accent="amber"
                                className="flex-1 min-w-0"
                              >
                                <select
                                  className="w-full px-2 py-1 bg-slate-900 border border-white/10 rounded text-white text-xs"
                                  value={selectedLandForProb || ''}
                                  onChange={(e) => setSelectedLandForProb(e.target.value || null)}
                                >
                                  <option value="" className="bg-slate-900 text-white">Select land...</option>
                                  {analysisCards.filter(c => c.isLand).map(card => (
                                    <option key={card.id} value={card.id} className="bg-slate-900 text-white">
                                      {card.quantity}x {card.name.length > 20 ? card.name.slice(0, 20) + '...' : card.name}
                                    </option>
                                  ))}
                                </select>
                              </BrewTooltip>
                              <BrewTooltip
                                title="Minimum Copies Needed"
                                badge={`≥${landProbMinCopies}`}
                                description="Minimum number of copies of this land you want to have drawn."
                                accent="amber"
                              >
                                <NumericStepper
                                  value={landProbMinCopies}
                                  min={1}
                                  max={Math.max(4, selectedLandForProb ? (analysisCards.find(c => c.id === selectedLandForProb)?.quantity || 4) : 4)}
                                  onChange={setLandProbMinCopies}
                                  size="xs"
                                  accent="amber"
                                  inputWidthClass="w-6"
                                  ariaLabel="Minimum land copies needed"
                                />
                              </BrewTooltip>
                            </div>
                            {extraLandSlots.map((slot, idx) => (
                              <div key={idx} className="flex items-center gap-1">
                                <select
                                  className="flex-1 min-w-0 px-2 py-1 bg-slate-900 border border-white/10 rounded text-white text-xs"
                                  value={slot.id || ''}
                                  onChange={(e) => {
                                    const next = [...extraLandSlots];
                                    next[idx] = { ...slot, id: e.target.value || null };
                                    setExtraLandSlots(next);
                                  }}
                                >
                                  <option value="" className="bg-slate-900 text-white">Select land #{idx + 2}...</option>
                                  {analysisCards.filter(c => c.isLand).map(card => (
                                    <option key={card.id} value={card.id} className="bg-slate-900 text-white">
                                      {card.quantity}x {card.name.length > 18 ? card.name.slice(0, 18) + '...' : card.name}
                                    </option>
                                  ))}
                                </select>
                                <NumericStepper
                                  value={slot.minCopies}
                                  min={1}
                                  max={Math.max(4, slot.id ? (analysisCards.find(c => c.id === slot.id)?.quantity || 4) : 4)}
                                  onChange={(val) => {
                                    const next = [...extraLandSlots];
                                    next[idx] = { ...slot, minCopies: val };
                                    setExtraLandSlots(next);
                                  }}
                                  size="xs"
                                  accent="amber"
                                  inputWidthClass="w-6"
                                  ariaLabel={`Minimum copies for land slot ${idx + 2}`}
                                />
                                <BrewTooltip title="Remove Slot" description="Removes this additional land requirement." accent="red">
                                  <button
                                    type="button"
                                    onClick={() => setExtraLandSlots(extraLandSlots.filter((_, i) => i !== idx))}
                                    className="px-1.5 py-0.5 text-xs text-red-400/70 hover:text-red-300 bg-red-500/10 hover:bg-red-500/20 rounded"
                                  >×</button>
                                </BrewTooltip>
                              </div>
                            ))}
                          </div>

                          {/* Hand Probability Output */}
                          {(() => {
                            const maxDeckHand = Math.max(1, analysisTotalCards || targetDeckSize || 60);
                            const safeHandSize = Math.max(0, Math.min(maxDeckHand, startingHandSize));
                            const maxTurnCalc = Math.max(1, maxDeckHand - safeHandSize + 1);
                            const safeTurn = Math.max(1, Math.min(maxTurnCalc, selectedTurn));
                            const handCardsSeen = Math.min(maxDeckHand, cardsSeenByTurn(safeTurn, onThePlay, 0, safeHandSize));
                            let combinedHandProb = 1;
                            let hasAnySelection = false;

                            const getPlanCopies = (cardId: string) => {
                              const card = analysisCards.find(c => c.id === cardId);
                              if (!card) return 0;
                              let planCopies = card.isSideboard ? 0 : card.quantity;
                              if (cardProbPlan !== 'base') {
                                const planKey = cardProbPlan as 'A' | 'B' | 'C' | 'D' | 'E';
                                const copySideboard = card.planCopySideboard?.[planKey] || [];
                                planCopies = 0;
                                for (let i = 0; i < card.quantity; i++) {
                                  const isFlagged = copySideboard[i] ?? false;
                                  const isInMain = card.isSideboard ? isFlagged : !isFlagged;
                                  if (isInMain) planCopies++;
                                }
                              }
                              return planCopies;
                            };

                            const applyCardSlot = (cardId: string | null, minNeeded: number) => {
                              if (!cardId) return;
                              hasAnySelection = true;
                              const planCopies = getPlanCopies(cardId);
                              if (planCopies >= minNeeded && analysisTotalCards > 0) {
                                combinedHandProb *= hypergeometricCDFTail(analysisTotalCards, planCopies, handCardsSeen, minNeeded);
                              } else {
                                combinedHandProb = 0;
                              }
                            };

                            applyCardSlot(selectedCardForProb, cardProbMinCopies);
                            extraCardSlots.forEach(s => applyCardSlot(s.id, s.minCopies));
                            applyCardSlot(selectedLandForProb, landProbMinCopies);
                            extraLandSlots.forEach(s => applyCardSlot(s.id, s.minCopies));

                            const color = hasAnySelection && combinedHandProb > 0 ? getProbabilityColorClass(combinedHandProb) : 'text-white/30';

                            return (
                              <div className="bg-white/5 rounded p-2 border border-white/10 space-y-2">
                                <div className="flex items-center justify-between">
                                  <span className="text-white/60 text-xs font-medium">Hand Probability</span>
                                  <span className={`text-lg font-bold font-mono ${color}`}>
                                    {hasAnySelection ? formatProbability(combinedHandProb) : '--'}
                                  </span>
                                </div>
                                <div className="grid grid-cols-2 gap-1.5">
                                  <BrewTooltip
                                    title="Target Turn"
                                    badge={`Turn ${safeTurn}`}
                                    description="Turn by which you want to have drawn your selected card(s). Use − / + or type any turn."
                                    accent="purple"
                                    className="block w-full"
                                  >
                                    <div className="flex items-center justify-between gap-1 bg-slate-900/70 border border-white/10 rounded px-1.5 py-1">
                                      <span className="text-[10px] text-white/50 font-semibold uppercase">Turn</span>
                                      <NumericStepper
                                        value={safeTurn}
                                        min={1}
                                        max={maxTurnCalc}
                                        onChange={(val) => {
                                          setSelectedTurn(val);
                                          setAnyLandTurn(val);
                                        }}
                                        size="xs"
                                        accent="purple"
                                        inputWidthClass="w-7"
                                        ariaLabel="Target turn for specific card probability"
                                      />
                                    </div>
                                  </BrewTooltip>
                                  <BrewTooltip
                                    title="Opening Hand Size"
                                    badge={`${safeHandSize} cards (max ${maxDeckHand})`}
                                    description={`Cards in your opening hand (0 up to ${maxDeckHand} cards in deck). Adjust with − / + or type directly.`}
                                    accent="cyan"
                                    className="block w-full"
                                  >
                                    <div className="flex items-center justify-between gap-1 bg-slate-900/70 border border-white/10 rounded px-1.5 py-1">
                                      <span className="text-[10px] text-white/50 font-semibold uppercase">Hand</span>
                                      <NumericStepper
                                        value={safeHandSize}
                                        min={0}
                                        max={maxDeckHand}
                                        onChange={(val) => {
                                          setStartingHandSize(val);
                                          setAnyLandHandSize(val);
                                        }}
                                        size="xs"
                                        accent="cyan"
                                        inputWidthClass="w-7"
                                        ariaLabel="Opening hand size"
                                      />
                                    </div>
                                  </BrewTooltip>
                                </div>
                                {/* Any Land Output */}
                                {(() => {
                                  const maxLandTarget = Math.max(0, analysisLandCount);
                                  const safeMinLands = Math.max(0, Math.min(Math.max(1, maxLandTarget), selectedMinLands));
                                  const landProb = analysisTotalCards > 0
                                    ? probabilityLandsByTurn(analysisTotalCards, analysisLandCount, safeMinLands, handCardsSeen)
                                    : 0;
                                  return (
                                    <div className="pt-2 border-t border-white/10 flex items-center justify-between gap-2">
                                      <BrewTooltip
                                        title="Any Land Count Target"
                                        badge={`≥${safeMinLands} of ${analysisLandCount}L`}
                                        description="Probability of seeing at least this many total lands from your deck by the selected turn."
                                        accent="amber"
                                      >
                                        <div className="flex items-center gap-1.5">
                                          <span className="text-amber-400 text-xs font-medium whitespace-nowrap">Any Land ≥</span>
                                          <NumericStepper
                                            value={safeMinLands}
                                            min={0}
                                            max={Math.max(1, maxLandTarget)}
                                            onChange={setSelectedMinLands}
                                            size="xs"
                                            accent="amber"
                                            inputWidthClass="w-7"
                                            ariaLabel="Minimum total lands needed"
                                          />
                                        </div>
                                      </BrewTooltip>
                                      <span className={`text-base font-bold font-mono ${getProbabilityColorClass(landProb)}`}>
                                        {formatProbability(landProb)}
                                      </span>
                                    </div>
                                  );
                                })()}
                              </div>
                            );
                          })()}
                        </div>

                        {/* Color Distribution Chart */}
                        <div className="flex-1 flex flex-col">
                          <span className="text-white/60 text-xs font-medium mb-1">Color Distribution</span>
                          <div className="flex-1 min-h-0 overflow-y-auto">
                            <ColorDistributionChart
                              deck={analysisCards}
                              onThePlay={onThePlay}
                              targetTurn={anyLandTurn}
                              onTargetTurnChange={setAnyLandTurn}
                              minLands={selectedMinLands}
                              onMinLandsChange={setSelectedMinLands}
                              handSize={anyLandHandSize}
                              onHandSizeChange={setAnyLandHandSize}
                              bottomedCardIds={bottomedCards}
                            />
                          </div>
                        </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()
            }

            {/* Sideboard - Dynamic view (simple grid layout like Lands) - excludes commanders, companions, extra cards */}
            {
              displaySideboard.filter(c => !c.isCommander && !c.isCompanion && !c.isExtraCard).length > 0 && onCardClick && (
                <div className="mt-4 rounded-lg border border-slate-500/20 overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium bg-gradient-to-r from-slate-500/20 to-purple-500/10 hover:from-slate-500/30 hover:to-purple-500/20 transition-colors"
                    onClick={() => setSideboardExpanded(!sideboardExpanded)}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-slate-300">Sideboard</span>
                      <span className="text-xs px-2 py-0.5 rounded bg-slate-500/20 text-slate-400">
                        {displaySideboard.filter(c => !c.isCommander && !c.isCompanion && !c.isExtraCard).reduce((sum, c) => sum + c.quantity, 0)} cards
                      </span>
                    </div>
                    <span className={`text-purple-400 text-xs transition-transform ${sideboardExpanded ? 'rotate-180' : ''}`}>▼</span>
                  </button>
                  {sideboardExpanded && (
                    <div className="p-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1">
                      {displaySideboard.filter(c => !c.isCommander && !c.isCompanion && !c.isExtraCard).map(card => (
                        <div
                          key={card.id}
                          className="h-[26px] flex items-center bg-slate-500/10 hover:bg-slate-500/20 rounded text-[9px] text-slate-300/90 cursor-pointer transition-colors"
                          onClick={() => onCardClick?.(card)}
                          onMouseEnter={(e) => {
                            setHoveredCard(card);
                            setMousePos({ x: e.clientX, y: e.clientY });
                          }}
                          onMouseMove={(e) => setMousePos({ x: e.clientX, y: e.clientY })}
                          onMouseLeave={() => setHoveredCard(null)}
                        >
                          {/* Minus button */}
                          <BrewTooltip
                            title="Remove 1 Copy"
                            description={`Removes 1 copy of ${card.name} from your sideboard.`}
                            accent="red"
                            className="h-full flex-shrink-0"
                          >
                            <button
                              type="button"
                              className="w-4 h-full flex items-center justify-center text-[10px] text-red-400/50 hover:text-red-400 hover:bg-red-500/30 rounded-l border-r border-slate-500/20 flex-shrink-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                const newQty = card.quantity - 1;
                                if (newQty <= 0) {
                                  onRemoveFromSideboard?.(card.id);
                                } else {
                                  onUpdateSideboardCard?.(card.id, { quantity: newQty });
                                }
                              }}
                            >−</button>
                          </BrewTooltip>

                          {/* Card name and quantity */}
                          <span
                            className="flex-1 text-center truncate px-1 cursor-pointer"
                            onClick={() => onCardClick(card)}
                          >
                            {card.quantity > 1 && <span className="text-slate-400/60">{card.quantity}× </span>}
                            {card.name}
                          </span>

                          {/* Plus button */}
                          <BrewTooltip
                            title="Add 1 Copy"
                            description={`Adds 1 copy of ${card.name} to your sideboard.`}
                            accent="emerald"
                            className="h-full flex-shrink-0"
                          >
                            <button
                              type="button"
                              className="w-4 h-full flex items-center justify-center text-[10px] text-green-400/50 hover:text-green-400 hover:bg-green-500/30 border-l border-slate-500/20 flex-shrink-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                onUpdateSideboardCard?.(card.id, { quantity: card.quantity + 1 });
                              }}
                            >+</button>
                          </BrewTooltip>

                          {/* MB button - bring into main deck */}
                          <BrewTooltip
                            title={activePlan === 'base' ? 'Move to Main Deck' : 'Board In for Match'}
                            badge="+MAIN"
                            description={activePlan === 'base' ? `Moves 1 copy of ${card.name} from your sideboard into your main deck.` : `Brings 1 copy of ${card.name} into your main deck for this matchup plan.`}
                            accent="cyan"
                            className="h-full flex-shrink-0"
                          >
                            <button
                              type="button"
                              className="w-6 h-full flex items-center justify-center text-[8px] text-cyan-400/50 hover:text-cyan-400 hover:bg-cyan-500/30 border-l border-cyan-500/20 rounded-r flex-shrink-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                const originalCard = sideboard.find(c => c.name === card.name);

                                if (activePlan === 'base') {
                                  if (originalCard && originalCard.quantity > 0) {
                                    const mainDeckCard = deck.find(c => c.name === originalCard.name);
                                    if (mainDeckCard) {
                                      onUpdateCard?.(mainDeckCard.id, { quantity: mainDeckCard.quantity + 1 });
                                    } else {
                                      onAddCard?.({ ...originalCard, id: `${originalCard.id}-main-${Date.now()}`, quantity: 1, isSideboard: false });
                                    }
                                    if (originalCard.quantity <= 1) {
                                      onRemoveFromSideboard?.(originalCard.id);
                                    } else {
                                      onUpdateSideboardCard?.(originalCard.id, { quantity: originalCard.quantity - 1 });
                                    }
                                  }
                                } else {
                                  const baseQty = originalCard?.quantity ?? card.quantity;
                                  const planKey = activePlan as 'A' | 'B' | 'C' | 'D' | 'E';
                                  const currentMain = originalCard?.planQuantities?.[planKey] ?? 0;
                                  const newMain = Math.min(currentMain + 1, baseQty);
                                  if (originalCard) {
                                    onUpdatePlanQuantity?.(originalCard.id, planKey, newMain);
                                  }
                                }
                              }}
                            >MB</button>
                          </BrewTooltip>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            }

            {/* Extra Cards (Other) - Dynamic view */}
            {
              [...deck, ...sideboard].some(c => c.isExtraCard) && onCardClick && (
                <div className="mt-4 bg-white/5 rounded-lg border border-white/10 p-3">
                  <div
                    className="flex items-center justify-between mb-2 cursor-pointer hover:bg-white/5 -mx-3 -mt-3 px-3 pt-3 pb-2 rounded-t-lg transition-colors"
                    onClick={() => setExtraExpanded(!extraExpanded)}
                  >
                    <h4 className="text-purple-400 text-sm font-medium flex items-center gap-2">
                      <span>Other</span>
                      <span className="text-xs px-2 py-0.5 rounded bg-purple-500/20 text-purple-400">
                        {[...deck, ...sideboard].filter(c => c.isExtraCard).reduce((sum, c) => sum + c.quantity, 0)} cards
                      </span>
                    </h4>
                    <span className={`text-purple-400 text-xs transition-transform duration-200 ${extraExpanded ? '' : '-rotate-90'}`}>▼</span>
                  </div>
                  {extraExpanded && (
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-1">
                      {[...deck, ...sideboard].filter(c => c.isExtraCard).map(card => (
                        <div
                          key={card.id}
                          className="h-[24px] px-2 flex items-center justify-center bg-purple-500/10 hover:bg-purple-500/20 rounded text-[9px] text-purple-400/90 cursor-pointer transition-colors"
                          onMouseEnter={(e) => {
                            setHoveredCard(card);
                            setMousePos({ x: e.clientX, y: e.clientY });
                          }}
                          onMouseMove={(e) => setMousePos({ x: e.clientX, y: e.clientY })}
                          onMouseLeave={() => setHoveredCard(null)}
                          onClick={() => onCardClick(card)}
                        >
                          {card.quantity > 1 && <span className="mr-1 text-purple-400/60">{card.quantity}×</span>}
                          <span className="truncate text-center">{card.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            }


            {/* Suggested Combo Pieces & Co-Occurrence Synergies Section */}
            <SuggestedCardsSection
              deck={deck}
              sideboard={sideboard}
              format={format}
              onAddCard={onAddCard}
              onAddToSideboard={onAddToSideboard}
            />

            {/* MV-Filtered Card Search Modal */}
            {
              buildSearchMV !== null && onAddCard && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
                  <div className="bg-[#0f172a] rounded-xl border border-white/20 p-6 w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-white font-medium">
                        Add Card at MV {buildSearchMV === 6 ? '6+' : buildSearchMV}
                        {activePlan !== 'base' && (
                          <span className="ml-2 text-xs px-2 py-0.5 rounded" style={{
                            backgroundColor: planColors?.[activePlan]?.bg,
                            color: planColors?.[activePlan]?.text
                          }}>
                            Match {activePlan === 'A' ? '1' : activePlan === 'B' ? '2' : activePlan === 'C' ? '3' : activePlan === 'D' ? '4' : '5'}
                          </span>
                        )}
                      </h3>
                      <button
                        onClick={() => setBuildSearchMV(null)}
                        className="text-white/40 hover:text-white"
                      >
                        <X className="size-5" />
                      </button>
                    </div>
                    <p className="text-white/40 text-xs mb-4">
                      Search for cards to add.{activePlan !== 'base' ? ` Cards placed here will have their functional MV set to ${buildSearchMV === 6 ? '6+' : buildSearchMV} for Plan ${activePlan}.` : ' Cards will use their actual mana value.'}
                      {mvParity !== 'all' && (
                        <span className={`ml-2 ${mvParity === 'odds' ? 'text-orange-400' : 'text-blue-400'}`}>
                          (Viewing: {mvParity === 'odds' ? 'Odd MVs only' : 'Even MVs only'})
                        </span>
                      )}
                    </p>
                    <div className="flex-1 overflow-auto">
                      <CardSearch
                        onAddCard={(card) => {
                          // Add card — only override MV when building for a specific plan
                          const isInPlan = activePlan !== 'base';
                          const overrideMV = buildSearchMV! >= 6 ? card.cmc : buildSearchMV!;
                          const modifiedCard = {
                            ...card,
                            // Only set effectiveMV in plan mode; base mode always uses real CMC
                            effectiveMV: isInPlan ? overrideMV : undefined,
                            // Set per-copy MV override for the active plan
                            planCopyMVs: isInPlan ? {
                              [activePlan as 'A' | 'B' | 'C' | 'D' | 'E']: [overrideMV]
                            } : undefined
                          };
                          onAddCard(modifiedCard);
                          // Don't close modal, allow adding multiple cards
                        }}
                        onAddToSideboard={onAddToSideboard}
                      />
                    </div>
                    <div className="mt-4 pt-4 border-t border-white/10 flex justify-end">
                      <button
                        onClick={() => setBuildSearchMV(null)}
                        className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded text-white text-sm transition-colors"
                      >
                        Done
                      </button>
                    </div>
                  </div>
                </div>
              )
            }
          </div>
        )
        }

        {
          activeTab === 'distribution' && (() => {
            // Filter out extra cards for distribution analysis
            const analysisCards = deck.filter(c => !c.isExtraCard);
            const analysisLandCount = analysisCards.filter(c => c.isLand).reduce((sum, c) => sum + c.quantity, 0);
            const analysisTotalCards = analysisCards.reduce((sum, c) => sum + c.quantity, 0);

            // Calculate probability for specific turn and land count
            const cardsSeen = cardsSeenByTurn(selectedTurn, onThePlay, 0, startingHandSize);
            const landProb = analysisTotalCards > 0
              ? probabilityLandsByTurn(analysisTotalCards, analysisLandCount, selectedMinLands, cardsSeen)
              : 0;
            const probability = analysisTotalCards > 0
              ? formatProbability(landProb)
              : '--';
            const probColorClass = analysisTotalCards > 0
              ? getProbabilityColorClass(landProb)
              : 'text-white/40';

            return (
              <div className="flex flex-col gap-3 h-full overflow-y-auto">
                {/* SECTION 1: Mana Curve Chart - Collapsible */}
                <div className="rounded-lg border border-purple-500/20">
                  <button
                    className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium bg-gradient-to-r from-purple-500/20 to-amber-500/10 hover:from-purple-500/30 hover:to-amber-500/20 transition-colors rounded-t-lg"
                    onClick={() => setCurveExpanded(!curveExpanded)}
                  >
                    <span className="text-amber-400">Mana Curve</span>
                    <span className={`text-purple-400 text-xs transition-transform ${curveExpanded ? 'rotate-180' : ''}`}>▼</span>
                  </button>
                  {curveExpanded && (
                    <div className="p-3 h-[320px]">
                      <ManaCurveChart
                        deck={analysisCards}
                        analysis={analysis}
                        inputMode="import"
                        activeAnchor={activePlan === 'base' ? 'Ideal' : activePlan as 'A' | 'B' | 'C' | 'D' | 'E'}
                        onAnchorChange={(anchor) => setActivePlan(anchor === 'Ideal' ? 'base' : anchor)}
                        targetDeckSize={targetDeckSize}
                        targetLands={effectiveTargetLands}
                        targetRatio={effectiveTargetRatio}
                        mvParity={mvParity}
                        excludeZero={excludeZero}
                        anchorDistribution={anchorDistribution}
                        landCount={effectiveTargetLands}
                        totalCards={targetDeckSize}
                        onThePlay={onThePlay}
                      />
                    </div>
                  )}
                </div>

                {/* SECTION 2: Probability Calculators - Collapsible */}
                <div className="rounded-lg border border-purple-500/20">
                  <button
                    className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium bg-gradient-to-r from-purple-500/20 to-amber-500/10 hover:from-purple-500/30 hover:to-amber-500/20 transition-colors rounded-t-lg"
                    onClick={() => setCalcExpanded(!calcExpanded)}
                  >
                    <span className="text-amber-400">🎲 Probability & On-Curve Land Calculators</span>
                    <span className={`text-purple-400 text-xs transition-transform ${calcExpanded ? 'rotate-180' : ''}`}>▼</span>
                  </button>
                  {calcExpanded && (
                    <div className="p-2.5 space-y-3">
                      <LandProbabilityTable
                        landCount={analysisLandCount}
                        totalCards={analysisTotalCards}
                        targetDeckSize={targetDeckSize}
                        targetLands={effectiveTargetLands}
                        onThePlay={onThePlay}
                        onTogglePlayDraw={setOnThePlay}
                        onApplyTargetLands={(lands) => setBuildTargetLands(lands)}
                      />
                        {/* Collapsible Specific Card Hand Odds & Color Distribution */}
                        <div className="rounded-lg border border-white/10 bg-slate-950/40 overflow-hidden">
                          <button
                            type="button"
                            onClick={() => setHandCalcExpanded(!handCalcExpanded)}
                            className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold bg-white/[0.04] hover:bg-white/[0.08] transition-colors"
                          >
                            <span className="text-purple-200">🎯 Specific Card Hand Odds & Color Distribution</span>
                            <span className={`text-purple-400 text-xs transition-transform ${handCalcExpanded ? 'rotate-180' : ''}`}>▼</span>
                          </button>
                          {handCalcExpanded && (
                            <div className="p-2.5 flex flex-col sm:flex-row gap-2 border-t border-white/10">
                      {/* Hand Probability Calculator */}
                      <div className="flex-1 flex flex-col gap-2">
                        {/* Shared Controls Row: Play/Draw + Match */}
                        <div className="flex items-center gap-2">
                          <button
                            className={`px-2 py-0.5 rounded text-xs font-medium transition-colors whitespace-nowrap ${onThePlay ? 'bg-purple-500/30 text-purple-400 border border-purple-500/30' : 'bg-amber-500/30 text-amber-400 border border-amber-500/30'}`}
                            onClick={() => setOnThePlay(!onThePlay)}
                          >
                            {onThePlay ? 'Play' : 'Draw'}
                          </button>
                          <select
                            className="flex-1 px-2 py-0.5 bg-white/5 border border-white/10 rounded text-white text-xs"
                            value={cardProbPlan}
                            onChange={(e) => setCardProbPlan(e.target.value as 'base' | 'A' | 'B' | 'C' | 'D' | 'E')}
                          >
                            <option value="base">Base</option>
                            <option value="A">M1</option>
                            <option value="B">M2</option>
                            <option value="C">M3</option>
                            <option value="D">M4</option>
                            <option value="E">M5</option>
                          </select>
                        </div>

                        {/* Specific Card Probability Section */}
                        <div className="bg-white/5 rounded p-2 border border-white/10">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-white/60 text-xs font-medium">Specific Card</span>
                            <button
                              className="text-xs px-1.5 py-0 rounded bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 disabled:opacity-30"
                              disabled={(1 + extraCardSlots.length + 1 + extraLandSlots.length) >= 8}
                              onClick={() => setExtraCardSlots([...extraCardSlots, { id: null, minCopies: 1 }])}
                              title="Add another card slot"
                            >+</button>
                          </div>

                          {/* Primary Card Selector */}
                          <div className="flex items-center gap-1 mb-1">
                            <select
                              className="flex-1 px-2 py-1 bg-white/5 border border-white/10 rounded text-white text-xs"
                              value={selectedCardForProb || ''}
                              onChange={(e) => setSelectedCardForProb(e.target.value || null)}
                            >
                              <option value="">Select spell...</option>
                              {analysisCards.filter(c => !c.isLand).map(card => (
                                <option key={card.id} value={card.id}>
                                  {card.quantity}x {card.name.length > 16 ? card.name.slice(0, 16) + '...' : card.name}
                                </option>
                              ))}
                            </select>
                            <div className="flex items-center bg-white/10 rounded border border-white/10" title="Min copies needed">
                              {[1, 2, 3, 4].map((n, i) => (
                                <button
                                  key={n}
                                  className={`w-5 py-0.5 text-xs font-medium ${i === 0 ? 'rounded-l' : ''} ${i === 3 ? 'rounded-r' : ''} ${cardProbMinCopies === n ? 'bg-purple-500/40 text-purple-300' : 'text-white/40 hover:text-white/60'}`}
                                  onClick={() => setCardProbMinCopies(n)}
                                >
                                  {n}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Extra Card Slots */}
                          {extraCardSlots.map((slot, i) => (
                            <div key={i} className="flex items-center gap-1 mb-1">
                              <select
                                className="flex-1 px-1 py-0.5 bg-white/5 border border-white/10 rounded text-white text-[8px] truncate"
                                value={slot.id || ''}
                                onChange={(e) => {
                                  const updated = [...extraCardSlots];
                                  updated[i] = { ...slot, id: e.target.value || null };
                                  setExtraCardSlots(updated);
                                }}
                              >
                                <option value="">Select spell...</option>
                                {analysisCards.filter(c => !c.isLand).map(card => (
                                  <option key={card.id} value={card.id}>
                                    {card.quantity}x {card.name.length > 12 ? card.name.slice(0, 12) + '...' : card.name}
                                  </option>
                                ))}
                              </select>
                              <div className="flex items-center bg-white/10 rounded" title="Min copies needed">
                                {[1, 2, 3, 4].map((n, j) => (
                                  <button
                                    key={n}
                                    className={`w-4 py-0.5 text-[8px] font-medium ${j === 0 ? 'rounded-l' : ''} ${j === 3 ? 'rounded-r' : ''} ${slot.minCopies === n ? 'bg-purple-500/40 text-purple-300' : 'text-white/40 hover:text-white/60'}`}
                                    onClick={() => {
                                      const updated = [...extraCardSlots];
                                      updated[i] = { ...slot, minCopies: n };
                                      setExtraCardSlots(updated);
                                    }}
                                  >
                                    {n}
                                  </button>
                                ))}
                              </div>
                              <button
                                className="text-red-400/50 hover:text-red-400 text-[8px] px-0.5"
                                onClick={() => setExtraCardSlots(extraCardSlots.filter((_, j) => j !== i))}
                              >×</button>
                            </div>
                          ))}
                        </div>

                        {/* Specific Land Probability Section */}
                        <div className="bg-white/5 rounded p-2 border border-white/10">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-white/60 text-xs font-medium">Specific Land</span>
                            <button
                              className="text-xs px-1.5 py-0 rounded bg-pink-500/20 text-pink-400 hover:bg-pink-500/30 disabled:opacity-30"
                              disabled={(1 + extraCardSlots.length + 1 + extraLandSlots.length) >= 8}
                              onClick={() => setExtraLandSlots([...extraLandSlots, { id: null, minCopies: 1 }])}
                              title="Add another land slot"
                            >+</button>
                          </div>

                          {/* Primary Land Selector */}
                          <div className="flex items-center gap-1 mb-1">
                            <select
                              className="flex-1 px-2 py-1 bg-white/5 border border-white/10 rounded text-white text-xs"
                              value={selectedLandForProb || ''}
                              onChange={(e) => setSelectedLandForProb(e.target.value || null)}
                            >
                              <option value="">Select land...</option>
                              {analysisCards.filter(c => c.isLand).map(card => (
                                <option key={card.id} value={card.id}>
                                  {card.quantity}x {card.name.length > 16 ? card.name.slice(0, 16) + '...' : card.name}
                                </option>
                              ))}
                            </select>
                            <div className="flex items-center bg-white/10 rounded border border-white/10" title="Min copies needed">
                              {[1, 2, 3, 4].map((n, i) => (
                                <button
                                  key={n}
                                  className={`w-5 py-0.5 text-xs font-medium ${i === 0 ? 'rounded-l' : ''} ${i === 3 ? 'rounded-r' : ''} ${landProbMinCopies === n ? 'bg-purple-500/40 text-purple-300' : 'text-white/40 hover:text-white/60'}`}
                                  onClick={() => setLandProbMinCopies(n)}
                                >
                                  {n}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Extra Land Slots */}
                          {extraLandSlots.map((slot, i) => (
                            <div key={i} className="flex items-center gap-1 mb-1">
                              <select
                                className="flex-1 px-1 py-0.5 bg-white/5 border border-white/10 rounded text-white text-[8px] truncate"
                                value={slot.id || ''}
                                onChange={(e) => {
                                  const updated = [...extraLandSlots];
                                  updated[i] = { ...slot, id: e.target.value || null };
                                  setExtraLandSlots(updated);
                                }}
                              >
                                <option value="">Select land...</option>
                                {analysisCards.filter(c => c.isLand).map(card => (
                                  <option key={card.id} value={card.id}>
                                    {card.quantity}x {card.name.length > 12 ? card.name.slice(0, 12) + '...' : card.name}
                                  </option>
                                ))}
                              </select>
                              <div className="flex items-center bg-white/10 rounded" title="Min copies needed">
                                {[1, 2, 3, 4].map((n, j) => (
                                  <button
                                    key={n}
                                    className={`w-4 py-0.5 text-[8px] font-medium ${j === 0 ? 'rounded-l' : ''} ${j === 3 ? 'rounded-r' : ''} ${slot.minCopies === n ? 'bg-purple-500/40 text-purple-300' : 'text-white/40 hover:text-white/60'}`}
                                    onClick={() => {
                                      const updated = [...extraLandSlots];
                                      updated[i] = { ...slot, minCopies: n };
                                      setExtraLandSlots(updated);
                                    }}
                                  >
                                    {n}
                                  </button>
                                ))}
                              </div>
                              <button
                                className="text-red-400/50 hover:text-red-400 text-[8px] px-0.5"
                                onClick={() => setExtraLandSlots(extraLandSlots.filter((_, j) => j !== i))}
                              >×</button>
                            </div>
                          ))}
                        </div>

                        {/* Hand Probability - Combined probability of all selections */}
                        {(() => {
                          // Calculate combined probability for all specific cards AND specific lands
                          const handCardsSeen = cardsSeenByTurn(selectedTurn, onThePlay, 0, startingHandSize);
                          let combinedHandProb = 1;
                          let hasAnySelection = false;

                          // Count bottomed cards to reduce effective deck size and copies
                          const bottomedCardCounts: Record<string, number> = {};
                          let totalBottomed = 0;
                          bottomedCards.forEach(cardId => {
                            if (cardId) {
                              bottomedCardCounts[cardId] = (bottomedCardCounts[cardId] || 0) + 1;
                              totalBottomed++;
                            }
                          });

                          // Effective deck size (minus bottomed cards)
                          const effectiveDeckSize = analysisTotalCards - totalBottomed;

                          // Helper to get plan copies for a card, minus any bottomed
                          const getPlanCopies = (cardId: string) => {
                            const card = analysisCards.find(c => c.id === cardId);
                            if (!card) return 0;
                            let planCopies = card.isSideboard ? 0 : card.quantity;
                            if (cardProbPlan !== 'base') {
                              const planKey = cardProbPlan as 'A' | 'B' | 'C' | 'D' | 'E';
                              const copySideboard = card.planCopySideboard?.[planKey] || [];
                              planCopies = 0;
                              for (let i = 0; i < card.quantity; i++) {
                                const isFlagged = copySideboard[i] ?? false;
                                const isInMain = card.isSideboard ? isFlagged : !isFlagged;
                                if (isInMain) planCopies++;
                              }
                            }
                            // Subtract bottomed copies of this card
                            const bottomedCount = bottomedCardCounts[cardId] || 0;
                            return Math.max(0, planCopies - bottomedCount);
                          };

                          // Include primary specific card
                          if (selectedCardForProb) {
                            hasAnySelection = true;
                            const planCopies = getPlanCopies(selectedCardForProb);
                            if (planCopies >= cardProbMinCopies) {
                              combinedHandProb *= hypergeometricCDFTail(effectiveDeckSize, planCopies, handCardsSeen, cardProbMinCopies);
                            } else {
                              combinedHandProb = 0;
                            }
                          }

                          // Include extra card slots
                          extraCardSlots.forEach((slot: { id: string | null; minCopies: number }) => {
                            if (slot.id) {
                              hasAnySelection = true;
                              const planCopies = getPlanCopies(slot.id);
                              if (planCopies >= slot.minCopies) {
                                combinedHandProb *= hypergeometricCDFTail(effectiveDeckSize, planCopies, handCardsSeen, slot.minCopies);
                              } else {
                                combinedHandProb = 0;
                              }
                            }
                          });

                          // Include primary specific land
                          if (selectedLandForProb) {
                            hasAnySelection = true;
                            const planCopies = getPlanCopies(selectedLandForProb);
                            if (planCopies >= landProbMinCopies) {
                              combinedHandProb *= hypergeometricCDFTail(effectiveDeckSize, planCopies, handCardsSeen, landProbMinCopies);
                            } else {
                              combinedHandProb = 0;
                            }
                          }

                          // Include extra land slots
                          extraLandSlots.forEach((slot: { id: string | null; minCopies: number }) => {
                            if (slot.id) {
                              hasAnySelection = true;
                              const planCopies = getPlanCopies(slot.id);
                              if (planCopies >= slot.minCopies) {
                                combinedHandProb *= hypergeometricCDFTail(effectiveDeckSize, planCopies, handCardsSeen, slot.minCopies);
                              } else {
                                combinedHandProb = 0;
                              }
                            }
                          });

                          const color = hasAnySelection && combinedHandProb > 0 ? getProbabilityColorClass(combinedHandProb) : 'text-white/30';

                          return (
                            <div className="bg-white/5 rounded p-2 border border-white/10">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-white/60 text-xs font-medium">Hand Probability</span>
                                <span className={`text-lg font-bold ${color}`}>
                                  {hasAnySelection ? formatProbability(combinedHandProb) : '--'}
                                </span>
                              </div>
                              {/* Turn selector */}
                              <div className="flex items-center gap-1 mb-1">
                                <span className="text-white/50 text-xs">T</span>
                                <div className="flex items-center bg-white/10 rounded border border-white/10">
                                  {[1, 2, 3, 4, 5, 6, 7, 8].map((t, i) => (
                                    <button
                                      key={t}
                                      className={`w-5 py-0.5 text-xs font-medium ${i === 0 ? 'rounded-l' : ''} ${i === 7 ? 'rounded-r' : ''} ${selectedTurn === t ? 'bg-purple-500/40 text-purple-300' : 'text-white/40 hover:text-white/60'}`}
                                      onClick={() => setSelectedTurn(t)}
                                    >
                                      {t}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              {/* Starting hand size selector */}
                              <div className="flex items-center gap-1">
                                <span className="text-white/50 text-xs" title="Starting hand size">Hand</span>
                                <div className="flex items-center bg-white/10 rounded border border-white/10">
                                  {[8, 7, 6, 5, 4, 3, 2, 1].map((h, i) => (
                                    <button
                                      key={h}
                                      className={`w-5 py-0.5 text-xs font-medium ${i === 0 ? 'rounded-l' : ''} ${i === 7 ? 'rounded-r' : ''} ${startingHandSize === h ? 'bg-purple-500/40 text-purple-300' : 'text-white/40 hover:text-white/60'}`}
                                      onClick={() => {
                                        setStartingHandSize(h);
                                        // Resize bottomedCards array to match new hand size
                                        const maxHand = onThePlay ? 7 : 8;
                                        const numBottomSlots = Math.max(0, maxHand - h);
                                        setBottomedCards(prev => {
                                          const newArr = [...prev];
                                          while (newArr.length < numBottomSlots) newArr.push(null);
                                          return newArr.slice(0, numBottomSlots);
                                        });
                                      }}
                                      title={h === 7 ? 'Keep 7' : h === 8 ? 'Draw 8' : `Mull to ${h}`}
                                    >
                                      {h}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              {/* Bottom cards slots (only shown when mulligan) */}
                              {(() => {
                                const maxHand = onThePlay ? 7 : 8; // Full hand depends on play/draw
                                const numBottomSlots = maxHand - startingHandSize;
                                if (numBottomSlots <= 0) return null;
                                return (
                                  <div className="mt-1.5 space-y-0.5">
                                    <span className="text-white/30 text-[6px]">Bottom of library:</span>
                                    {Array.from({ length: numBottomSlots }, (_, slotIndex) => (
                                      <div key={slotIndex} className="flex items-center gap-1">
                                        <span className="text-white/20 text-[7px] w-[12px]">{slotIndex + 1}.</span>
                                        <select
                                          value={bottomedCards[slotIndex] || ''}
                                          onChange={(e) => {
                                            const cardId = e.target.value || null;
                                            setBottomedCards(prev => {
                                              const newArr = [...prev];
                                              while (newArr.length <= slotIndex) newArr.push(null);
                                              newArr[slotIndex] = cardId;
                                              return newArr;
                                            });
                                          }}
                                          className="flex-1 bg-black/40 border border-white/10 rounded text-[7px] text-white/60 px-1 py-0.5"
                                        >
                                          <option value="">Select card...</option>
                                          {analysisCards
                                            .filter(c => !c.isLand && !c.isSideboard)
                                            .sort((a, b) => a.name.localeCompare(b.name))
                                            .map(card => (
                                              <option key={card.id} value={card.id}>{card.name}</option>
                                            ))}
                                          <optgroup label="Lands">
                                            {analysisCards
                                              .filter(c => c.isLand && !c.isSideboard)
                                              .sort((a, b) => a.name.localeCompare(b.name))
                                              .map(card => (
                                                <option key={card.id} value={card.id}>{card.name}</option>
                                              ))}
                                          </optgroup>
                                        </select>
                                      </div>
                                    ))}
                                  </div>
                                );
                              })()}

                              {/* Any Land Output */}
                              {(() => {
                                // Account for bottomed cards
                                const totalBottomed = bottomedCards.filter(id => id !== null).length;
                                const baseDeckSize = analysisTotalCards;
                                const effectiveDeckSize = baseDeckSize - totalBottomed;

                                // Count bottomed lands
                                let bottomedLands = 0;
                                bottomedCards.forEach(cardId => {
                                  if (cardId) {
                                    const card = analysisCards.find(d => d.id === cardId);
                                    if (card?.isLand) bottomedLands++;
                                  }
                                });

                                const effectiveLandCount = analysisLandCount - bottomedLands;
                                const cardsSeen = cardsSeenByTurn(selectedTurn, onThePlay, 0, startingHandSize);
                                const landProb = effectiveDeckSize > 0
                                  ? probabilityLandsByTurn(effectiveDeckSize, effectiveLandCount, selectedMinLands, cardsSeen)
                                  : 0;

                                return (
                                  <div className="mt-2 pt-2 border-t border-white/10">
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="text-amber-400 text-xs font-medium">Any Land ({effectiveLandCount})</span>
                                      <span className={`text-lg font-bold font-mono ${getProbabilityColorClass(landProb)}`}>
                                        {formatProbability(landProb)}
                                      </span>
                                    </div>
                                    {/* Min lands selector */}
                                    <div className="flex items-center gap-1">
                                      <span className="text-white/50 text-xs">≥</span>
                                      <div className="flex items-center bg-white/10 rounded border border-white/10">
                                        {[1, 2, 3, 4, 5, 6].map((n, i) => (
                                          <button
                                            key={n}
                                            className={`w-5 py-0.5 text-xs font-medium ${i === 0 ? 'rounded-l' : ''} ${i === 5 ? 'rounded-r' : ''} ${selectedMinLands === n ? 'bg-amber-500/40 text-amber-300' : 'text-white/40 hover:text-white/60'}`}
                                            onClick={() => setSelectedMinLands(n)}
                                          >
                                            {n}
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          );
                        })()}
                      </div>

                      {/* Color Distribution Chart */}
                      <div className="flex-1 flex flex-col">
                        <span className="text-white/60 text-xs font-medium mb-1">Color Distribution</span>
                        <div className="flex-1 min-h-0 overflow-y-auto">
                          <ColorDistributionChart
                            deck={analysisCards}
                            onThePlay={onThePlay}
                            targetTurn={anyLandTurn}
                            onTargetTurnChange={setAnyLandTurn}
                            minLands={selectedMinLands}
                            onMinLandsChange={setSelectedMinLands}
                            handSize={anyLandHandSize}
                            onHandSizeChange={setAnyLandHandSize}
                            bottomedCardIds={bottomedCards}
                          />
                        </div>
                      </div>
                            </div>
                          )}
                        </div>
                      </div>
                  )}
                </div>
              </div>
            );
          })()
        }

        {/* LANDING Tab */}
        {
          activeTab === 'landing' && (
            <LandingView
              onContinueAsGuest={() => setActiveTab('news')}
              onOpenAuth={() => {
                if (onOpenAuth) {
                  onOpenAuth();
                } else if ((window as any).__openAuthModal) {
                  (window as any).__openAuthModal();
                }
              }}
              onNavigateTab={(tabId) => setActiveTab(tabId as TabType)}
            />
          )
        }

        {/* NEWS Tab */}
        {
          activeTab === 'news' && (
            <NewsTab />
          )
        }

        {/* EVENTS Tab */}
        {
          activeTab === 'events' && (
            <EventsTab />
          )
        }

        {
          activeTab === 'queue' && (
            <div className="flex flex-col items-center justify-center h-64 text-white/40">
              <Users className="size-12 mb-4 opacity-20" />
              <h3 className="text-lg font-medium text-white/60 mb-2">Viewer Queue</h3>
              <p className="text-sm text-center max-w-md">
                A streamer-style probability-based slot machine for community game selection with player queue management.
              </p>
              <span className="mt-4 px-3 py-1 bg-white/5 rounded text-xs">Coming Soon</span>
            </div>
          )
        }
        {
          activeTab === 'session' && (
            <SessionTab />
          )
        }
        {
          activeTab === 'discover' && (
            <DiscoverTab
              deck={displayDeck}
              deckName={deckName}
              deckSize={targetDeckSize}
              landCount={effectiveTargetLands}
              manaCurve={targetManaCurve}
              onLoadDeck={onLoadDeckFromDiscover}
              onNavigateToSession={(deckData) => {
                // Store deck data for SESSION to pick up
                localStorage.setItem('deckstr_session_deck', JSON.stringify(deckData));
                // Switch to SESSION tab
                setActiveTab('session');
              }}
            />
          )
        }
        {
          activeTab === 'market' && (
            <MarketTab
              deck={displayDeck}
              sideboard={sideboard}
              deckName={deckName}
            />
          )
        }
        {
          activeTab === 'faq' && (
            <FAQPage />
          )
        }
      </div >

      {/* Import Modal */}
      {
        showImportModal && onAddCard && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <div className="bg-[#0f172a] rounded-xl border border-white/20 p-6 w-full max-w-2xl max-h-[80vh] overflow-auto">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-white text-lg font-semibold">Import Deck</h2>
                <button
                  onClick={() => setShowImportModal(false)}
                  className="text-white/60 hover:text-white p-1"
                >
                  <X className="size-5" />
                </button>
              </div>
              <DecklistImport
                currentDeck={deck}
                onImport={(cards) => {
                  // Clear existing deck before importing
                  onClearDeck?.();
                  cards.forEach(onAddCard);
                  setShowImportModal(false);
                }}
                onImportSideboard={onAddToSideboard ? (cards) => {
                  // Note: onClearDeck already clears sideboard too
                  cards.forEach(onAddToSideboard);
                  setShowImportModal(false);
                } : undefined}
                onFormatDetect={onFormatDetect}
                onDeckSizeDetect={setBuildTargetDeckSize}
              />
              <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-white/10">
                <button
                  onClick={() => setShowImportModal(false)}
                  className="px-4 py-2 text-sm text-white/60 hover:text-white"
                >
                  Back
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Plan Popup Modal - for per-copy cost assignment */}
      {
        planPopup && (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
            onClick={() => setPlanPopup(null)}
          >
            <div
              className="bg-[#0a0f1a] border border-white/20 rounded-xl p-4 min-w-[200px] max-w-[280px]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-3">
                <div>
                  <span
                    className="text-lg font-medium"
                    style={{ color: planColors[planPopup.plan]?.text }}
                  >
                    Plan {planPopup.plan}
                  </span>
                  <span className="text-white/50 text-sm ml-2">{planPopup.card.name}</span>
                </div>
                <button
                  onClick={() => setPlanPopup(null)}
                  className="text-white/40 hover:text-white p-1"
                >
                  <X className="size-4" />
                </button>
              </div>

              {/* Per-copy inputs */}
              <div className="space-y-1.5 mb-4">
                <div className="text-[10px] text-white/40 uppercase tracking-wide mb-2">
                  Cost per copy
                </div>
                {planPopup.mvs.map((mv, i) => {
                  const isFlagged = planPopup.sbs[i] ?? false;
                  return (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-xs text-white/50 w-12">Copy {i + 1}</span>
                      <div
                        className="flex-1 flex items-center rounded border overflow-hidden"
                        style={{
                          backgroundColor: planColors[planPopup.plan]?.bg,
                          borderColor: planColors[planPopup.plan]?.border
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            const newMVs = [...planPopup.mvs];
                            newMVs[i] = Math.max(0, (newMVs[i] ?? 0) - 1);
                            setPlanPopup({ ...planPopup, mvs: newMVs });
                          }}
                          disabled={mv <= 0}
                          className="w-7 py-1 text-sm font-bold text-white/70 hover:text-white hover:bg-white/10 disabled:opacity-30 transition-colors border-r border-white/10 select-none"
                          aria-label={`Decrease Copy ${i + 1} mana value`}
                        >
                          −
                        </button>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={mv}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/[^0-9]/g, '');
                            const newMVs = [...planPopup.mvs];
                            newMVs[i] = raw === '' ? 0 : Math.max(0, Math.min(99, parseInt(raw, 10) || 0));
                            setPlanPopup({ ...planPopup, mvs: newMVs });
                          }}
                          className="flex-1 min-w-0 bg-transparent text-center text-sm font-mono py-1 px-1 focus:outline-none"
                          style={{ color: planColors[planPopup.plan]?.text }}
                          aria-label={`Copy ${i + 1} mana value`}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newMVs = [...planPopup.mvs];
                            newMVs[i] = Math.min(99, (newMVs[i] ?? 0) + 1);
                            setPlanPopup({ ...planPopup, mvs: newMVs });
                          }}
                          className="w-7 py-1 text-sm font-bold text-white/70 hover:text-white hover:bg-white/10 transition-colors border-l border-white/10 select-none"
                          aria-label={`Increase Copy ${i + 1} mana value`}
                        >
                          +
                        </button>
                      </div>
                      {/* SB/MB toggle - shows SB for mainboard cards, MB for sideboard cards */}
                      <BrewTooltip
                        title={planPopup.card.isSideboard ? (isFlagged ? 'Boarded In (Main)' : 'Keep in Sideboard') : (isFlagged ? 'Boarded Out (Side)' : 'Keep in Mainboard')}
                        badge={planPopup.card.isSideboard ? 'MB' : 'SB'}
                        description={planPopup.card.isSideboard
                          ? (isFlagged ? 'This copy is brought into the mainboard for this matchup.' : 'Click to bring this copy into your mainboard for this matchup.')
                          : (isFlagged ? 'This copy is moved to the sideboard for this matchup.' : 'Click to move this copy to the sideboard for this matchup.')
                        }
                        accent={planPopup.card.isSideboard ? 'cyan' : 'amber'}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            const newSBs = [...planPopup.sbs];
                            newSBs[i] = !newSBs[i];
                            setPlanPopup({ ...planPopup, sbs: newSBs });
                          }}
                          className={`text-[9px] px-1.5 py-1 rounded font-medium transition-colors ${planPopup.card.isSideboard
                            ? (isFlagged
                              ? 'bg-cyan-500/30 text-cyan-400 border border-cyan-500/50'
                              : 'bg-white/5 text-white/30 border border-white/10 hover:bg-white/10')
                            : (isFlagged
                              ? 'bg-amber-500/30 text-amber-400 border border-amber-500/50'
                              : 'bg-white/5 text-white/30 border border-white/10 hover:bg-white/10')
                            }`}
                        >
                          {planPopup.card.isSideboard ? 'MB' : 'SB'}
                        </button>
                      </BrewTooltip>
                    </div>
                  );
                })}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between">
                <button
                  onClick={() => {
                    const baseMV = planPopup.card.effectiveMV ?? planPopup.card.cmc;
                    setPlanPopup({
                      ...planPopup,
                      mvs: Array(planPopup.card.quantity).fill(baseMV),
                      sbs: Array(planPopup.card.quantity).fill(false)
                    });
                  }}
                  className="text-[10px] text-white/40 hover:text-white/60"
                >
                  Reset
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPlanPopup(null)}
                    className="px-3 py-1 text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 rounded"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      onUpdatePlanCopyMVs?.(planPopup.card.id, planPopup.plan, planPopup.mvs);
                      onUpdatePlanCopySideboard?.(planPopup.card.id, planPopup.plan, planPopup.sbs);
                      setPlanPopup(null);
                    }}
                    className="px-3 py-1 text-xs text-white rounded"
                    style={{ backgroundColor: planColors[planPopup.plan]?.hex }}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }


      {/* Floating Card Preview on Hover - BUILD tab only */}
      {activeTab === 'build' && hoveredCard && (
        <HoverCardPreview key={`${hoveredCard.id}-${hoveredCard.isFlipped ? 'back' : 'front'}`} hoveredCard={hoveredCard} mousePos={mousePos} />
      )}

      {/* Save Deck Modal */}
      <SaveDeckModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onSave={async (name, format, coverImage) => {
          // Dynamic import to avoid SSR issues
          const { deckService } = await import('../lib/supabase');

          // Get current user
          const user = await authService.getCurrentUser();

          // Format cards for database
          const cardsRecord: Record<string, number> = {};
          const landsRecord: Record<string, number> = {};
          const sideboardRecord: Record<string, number> = {};

          deck.forEach(card => {
            if (card.isLand) {
              landsRecord[card.name] = (landsRecord[card.name] || 0) + card.quantity;
            } else {
              cardsRecord[card.name] = (cardsRecord[card.name] || 0) + card.quantity;
            }
          });

          sideboard.forEach(card => {
            if (!card.isCommander && !card.isCompanion) {
              sideboardRecord[card.name] = (sideboardRecord[card.name] || 0) + card.quantity;
            }
          });

          // Get color identity from deck
          const colors = [...new Set(deck.flatMap(c => c.colorIdentity || []))];

          // Find commander and companion
          const commander = sideboard.find(c => c.isCommander);
          const companion = sideboard.find(c => c.isCompanion);

          // Get author info from user
          const authorName = user?.user_metadata?.display_name
            || user?.user_metadata?.name
            || user?.user_metadata?.full_name
            || user?.email?.split('@')[0]
            || 'Anonymous';

          // Get user role if logged in
          const userRole = user ? await roleService.getCurrentUserRole() : 'user';

          // Build plans configuration from per-card planQuantities AND planCopyMVs
          // Format: { cardName: { base: qty, A: qty, B: qty, ..., mvs?: { A: [...], B: [...], ... } } }
          const plansConfig: Record<string, Record<string, any>> = {};

          // Process mainboard first to establish base quantities
          deck.forEach(card => {
            const hasQuantityPlans = card.planQuantities && Object.keys(card.planQuantities).length > 0;
            const hasMVPlans = card.planCopyMVs && Object.keys(card.planCopyMVs).length > 0;

            if (hasQuantityPlans || hasMVPlans) {
              plansConfig[card.name] = {
                base: card.quantity, // Mainboard card - use actual quantity
                ...(card.planQuantities || {}),
              };

              if (hasMVPlans) {
                plansConfig[card.name].mvs = card.planCopyMVs;
              }
            }
          });

          // Process sideboard - merge with existing data, don't overwrite base
          sideboard.forEach(card => {
            const hasQuantityPlans = card.planQuantities && Object.keys(card.planQuantities).length > 0;
            const hasMVPlans = card.planCopyMVs && Object.keys(card.planCopyMVs).length > 0;

            if (hasQuantityPlans || hasMVPlans) {
              if (!plansConfig[card.name]) {
                // Card only in sideboard
                plansConfig[card.name] = {
                  base: 0, // Sideboard-only card
                  ...(card.planQuantities || {}),
                };
              } else {
                // Card exists in mainboard too - merge plan quantities
                // Don't overwrite base (mainboard quantity takes precedence)
                if (card.planQuantities) {
                  Object.entries(card.planQuantities).forEach(([plan, qty]) => {
                    // Only add sideboard plan qty if mainboard doesn't have one
                    if (plansConfig[card.name][plan] === undefined && qty !== undefined) {
                      plansConfig[card.name][plan] = qty;
                    }
                  });
                }
              }

              // Merge MV overrides
              if (hasMVPlans) {
                if (!plansConfig[card.name].mvs) {
                  plansConfig[card.name].mvs = {};
                }
                Object.entries(card.planCopyMVs!).forEach(([plan, mvs]) => {
                  if (!plansConfig[card.name].mvs[plan]) {
                    plansConfig[card.name].mvs[plan] = mvs;
                  }
                });
              }
            }
          });

          const hasAnyPlans = Object.keys(plansConfig).length > 0;

          await deckService.saveDeck({
            name,
            format,
            cover_image: coverImage,
            cards: cardsRecord,
            lands: landsRecord,
            sideboard: sideboardRecord,
            colors,
            commander: commander ? { name: commander.name, count: commander.quantity } : null,
            companion: companion ? { name: companion.name, count: companion.quantity } : null,
            author_name: authorName,
            author_id: user?.id || null,
            author_role: userRole,
            user_id: user?.id || null,
            description: '',
            is_public: true,
            deck_size: deck.reduce((sum, c) => sum + c.quantity, 0),
            land_count: deck.filter(c => c.isLand).reduce((sum, c) => sum + c.quantity, 0),
            mana_curve: displaySpellCount > 0 ? displayTotalMV / displaySpellCount : 0,
            plans: hasAnyPlans ? plansConfig : null,
          });

          // Award sparks for saving a deck (+2)
          if (user) {
            sparksService.awardDeckSave('brew-save').catch(() => { });
          }
        }}
        deck={deck}
        defaultName=""
        defaultFormat="standard"
      />
    </div >
  );
}
